import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
  TextField,
  Divider,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import BackspaceIcon from '@mui/icons-material/Backspace';
import DeleteIcon from '@mui/icons-material/Delete';
import { PaymentOption } from './QuickSaleScreenSettingsModal';
import { getFiscalDevice } from '../../hooks/fiscalDevices';
import type { IngenicoOpenTransactionData } from '../../hooks/fiscalDevices/ingenico';
import { attemptAutoReconnect as attemptIngenicoReconnect } from '../../hooks/fiscalDevices/ingenico';
import PavoLiveBadge from '../PavoLiveBadge';
import GuideButton from '../../../../common/guide/GuideButton';
import { v4 as uuidv4 } from 'uuid';
// ✅ PavoPay için özel fonksiyonlar
import { 
  startSaleWithItems as pavoPayStartSale, 
  addPaymentToSale as pavoPayAddPayment, 
  addPaymentAndFinalizeSale as pavoPayAddPaymentAndFinalize, // ✅ Son ödeme için
  finalizePavoPaySale, 
  abandonPavoPaySale,
  savePavoPaySale
} from '../../hooks/fiscalDevices/pavopay';
import { getPavoCardMediator } from '../../../../../services/pavoMediatorConfig';

// Types
export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  vatRate?: number;
  vatStatus?: 'included' | 'excluded'; // ✅ YENİ: KDV durumu
}

export interface InventoryItem {
  id: string;
  name: string;
  departmentNo?: number;
  vatRate?: number; // ✅ YENİ: KDV oranı
}

interface PartialPaymentModalProps {
  open: boolean;
  onClose: (paymentDetails: PaymentDetails | null) => void;
  totalAmount: number;
  companyId: string | null;
  screenId: string | null;
  cashOptionLabel: string;
  cashOptionCashboxId: string | null;
  posOptionsList: PaymentOption[];
  openAccountOption?: PaymentOption | null;
  isGeneralCustomer: boolean;
  cart: CartItem[];
  selectedFiscalDevice: 'hugin_t300' | 'inpos_m530' | 'beko_x30tr' | 'beko_300tr' | 'pavopay' | 'ingenico_5000f';
  inventoryItems: InventoryItem[];
  showAlertModal: (title: string, message: string) => void;
  setIsProcessingPosPayment: (processing: boolean) => void; // ✅ Mavi modal için
  isRestaurantMode?: boolean; // ✅ YENİ: Restoran modunda fiyatlar zaten KDV dahil
  ingenicoRecoveryData?: IngenicoOpenTransactionData | null;
  useExternalPrinterForPavo?: boolean;
  kitchenOrderCode?: string;
  receiptCompanyName?: string;
  // ✅ Rest-P2P (pavo_rest_p2p) — başlıkta canlı rozet
  isRestP2PEnabled?: boolean;
  restP2PDeviceCode?: string | null;
  restP2PDeviceName?: string | null;
  // 🎫 NUMARA GARANTİSİ: Modal içinde cihazdan gerçek bir ödeme denemesi yapıldıktan
  // sonra satış TAMAMLANMADAN modal kapatılırsa çağrılır. QuickSaleScreen bu callback
  // ile sipariş numarasını yeniler (denenen numara yakılır, tekrar kullanılmaz).
  onPaymentAttemptFailed?: () => void;
}

export interface PaymentDetails {
  paidByCash: number;
  paidByPosMap: { [posId: string]: number };
  paidByOpenAccount: number;
  totalPaid: number;
  remainingAmount: number;
  changeAmount: number;
  fiscalProcessed?: boolean; // ✅ YENİ: Yazarkasa işlemi yapıldı mı?
  fiscalData?: any; // ✅ YENİ: Yazarkasa verisi
  // ✅ YENİ (Rest-P2P): paidByPosMap aynı POS'a yapılan birden fazla ödemeyi
  // tek anahtarda topluyor; bu nedenle "3 ayrı kart okutması" senaryosu
  // kayboluyor. Bu alan, Rest-P2P akışında her ödemeyi AYRI bir part olarak
  // Pavo cihazına iletmek için kullanılır. Sadece Rest-P2P akışı tarafından
  // tüketilir; SQL/atomik kayıt, fiscal device akışları paidByPosMap'i
  // okumaya devam eder (geriye dönük tam uyumluluk).
  pavoSplitPosParts?: Array<{ posId: string; amount: number; label?: string }>;
  // ✅ Kulüp girişi: atomik akış normalde 'PARTIAL' kaydeder; fakat aktif
  // girişlerde "ödeme" sütunu POS/Nakit ayrımı gösterir. Bu override ile
  // giriş ödeme yöntemi (basılan butona göre) doğru kaydedilir.
  clubEntryPaymentMethodOverride?: 'CASH' | 'POS' | 'PARTIAL';
}

// ✅ YENİ: Ödeme geçmişi için interface
interface PaymentHistoryItem {
  id: string;
  type: 'cash' | 'pos' | 'open_account';
  label: string;
  amount: number;
  timestamp: number;
  posId?: string; // POS ödemeleri için
  status: 'pending' | 'paid' | 'refunded'; // ✅ YENİ: Ödeme durumu
  fiscalData?: any; // ✅ YENİ: Yazarkasa verisi (authCode, batchNo, vb.)
  paymentSequenceNo?: number; // ✅ YENİ: Yazarkasa ödeme sıra numarası (iade için)
}

// ✅ YENİ: Ödeme adımı takip interface
interface PaymentStep {
  stepIndex: number;
  type: 'pos' | 'cash';
  label: string;
  amount: number;
  status: 'waiting' | 'processing' | 'success' | 'error';
  errorMessage?: string;
  fiscalData?: any;
}

const NumpadButton = ({ value, onClick, xs = 3, variant = "outlined", color = "primary", disabled = false, isSmallScreen }: { value: string, onClick: (value: string) => void, xs?: number, variant?: "text" | "outlined" | "contained", color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning", disabled?: boolean, isSmallScreen?: boolean }) => (
  <Grid item xs={xs}>
    <Button
      fullWidth
      variant={variant}
      color={color}
      onClick={() => onClick(value)}
      disabled={disabled}
      sx={{
        height: isSmallScreen ? '50px' : '60px',
        fontSize: isSmallScreen ? '1rem' : '1.2rem',
      }}
    >
      {value === 'backspace' ? <BackspaceIcon /> : value}
    </Button>
  </Grid>
);

const PartialPaymentModal: React.FC<PartialPaymentModalProps> = ({
  open,
  onClose,
  totalAmount,
  companyId,
  screenId,
  cashOptionLabel,
  cashOptionCashboxId,
  posOptionsList,
  openAccountOption,
  isGeneralCustomer,
  cart,
  selectedFiscalDevice,
  inventoryItems,
  showAlertModal,
  setIsProcessingPosPayment, // ✅ Mavi modal için
  isRestaurantMode = false, // ✅ YENİ: Restoran modunda fiyatlar zaten KDV dahil
  ingenicoRecoveryData,
  useExternalPrinterForPavo = false,
  kitchenOrderCode,
  receiptCompanyName,
  isRestP2PEnabled = false,
  restP2PDeviceCode = null,
  restP2PDeviceName = null,
  onPaymentAttemptFailed,
}) => {
  const [restP2POnline, setRestP2POnline] = useState<boolean>(false);
  const [currentInput, setCurrentInput] = useState('');
  const [paidByCash, setPaidByCash] = useState(0);
  const [paidByPosMap, setPaidByPosMap] = useState<{ [posId: string]: number }>({});
  const [paidByOpenAccount, setPaidByOpenAccount] = useState(0);
  const [activePaymentType, setActivePaymentType] = useState<string | null>(null);
  
  // ✅ YENİ: Ödeme geçmişi
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  
  // ✅ YENİ: Fiscal data'yı sakla
  const [lastFiscalData, setLastFiscalData] = useState<any>(null);
  
  // ✅ Silme modalı
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  
  // ✅ YENİ: Belge iptal onay modalı
  const [isCancelDocumentConfirmOpen, setIsCancelDocumentConfirmOpen] = useState(false);

  // ✅ YENİ: Ödeme adımları için state'ler
  const [paymentSteps, setPaymentSteps] = useState<PaymentStep[]>([]);
  const [isProcessingSteps, setIsProcessingSteps] = useState(false);
  const [currentFailedStep, setCurrentFailedStep] = useState<number | null>(null);
  const [documentIdForCancel, setDocumentIdForCancel] = useState<string | null>(null);

  // ✅ YENİ: "Canlı Belge" Modu için state'ler
  const [isLiveDocumentMode, setIsLiveDocumentMode] = useState(false); // Canlı belge modu aktif mi?
  const [isDocumentPrepared, setIsDocumentPrepared] = useState(false); // Belge hazırlandı mı?
  const [liveDocumentStartTime, setLiveDocumentStartTime] = useState<number | null>(null); // Belge başlama zamanı
  const [processingPaymentId, setProcessingPaymentId] = useState<string | null>(null); // İşlenen ödeme ID'si
  const [paymentSequenceCounter, setPaymentSequenceCounter] = useState(1); // Ödeme sıra sayacı

  // ✅ YENİ: Gerçek İade Modalı için state'ler
  const [isRealRefundModalOpen, setIsRealRefundModalOpen] = useState(false);
  const [refundQueue, setRefundQueue] = useState<PaymentHistoryItem[]>([]); // İade edilecek ödemeler
  const [currentRefundIndex, setCurrentRefundIndex] = useState(0); // Şu anda iade edilen ödeme indexi
  const [refundResults, setRefundResults] = useState<{ success: boolean; message: string }[]>([]); // İade sonuçları

  // ✅ YENİ: PavoPay için parçalı ödeme state'leri
  const [pavoPaySaleNumber, setPavoPaySaleNumber] = useState<string | null>(null); // Aktif PavoPay satış numarası
  const [pavoPayGrossPrice, setPavoPayGrossPrice] = useState<number | null>(null); // PavoPay'in kabul ettiği GrossPrice

  // ✅ YENİ: Yazarkasa bağlı değil onay modalı için state'ler
  const [isFiscalDisconnectedConfirmOpen, setIsFiscalDisconnectedConfirmOpen] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState<(() => void) | null>(null);
  const [skipFiscalDevice, setSkipFiscalDevice] = useState(false); // Yazarkasa atla flag'i

  // ✅ YENİ (Ingenico): Ödeme ekleme hatasında cihaz durumu doğrulanamadıysa
  // "Yine de Devam Et" onayı — kullanıcı ödemenin alındığını biliyorsa ödemeyi alınmış sayar.
  const [failedPaymentForContinue, setFailedPaymentForContinue] = useState<PaymentHistoryItem | null>(null);
  const [failedPaymentErrorMsg, setFailedPaymentErrorMsg] = useState<string>('');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // ───────────────────────────────────────────────
  // 🎫 NUMARA GARANTİSİ (modal içi cihaz denemeleri)
  // ───────────────────────────────────────────────
  // Modal, yazarkasa/PavoPay ödeme çağrılarını KENDİ İÇİNDE yapıyor. Deneme
  // başarısız olursa modal açık kalır ve kullanıcı aynı satışa devam edebilir —
  // bu sırada numara DEĞİŞMEZ (fiş/mutfak numarası tutarlılığı için). Ancak
  // cihazdan gerçek bir ödeme denemesi yapıldıktan sonra satış tamamlanmadan
  // modal kapatılırsa (vazgeçme / belge iptali), denenen sipariş numarası
  // yakılmalı ki bir sonraki satışta aynı numara tekrar kullanılmasın.
  const fiscalAttemptMadeRef = useRef(false);

  /** Cihazdan ödeme alınmaya başlanmadan hemen önce çağrılır. */
  const markFiscalAttempt = () => {
    fiscalAttemptMadeRef.current = true;
  };

  /** Modalı ödeme TAMAMLANMADAN kapatır; deneme yapıldıysa numara yenilemesi tetiklenir. */
  const closeWithoutSuccess = () => {
    const attempted = fiscalAttemptMadeRef.current;
    fiscalAttemptMadeRef.current = false;
    if (attempted && onPaymentAttemptFailed) {
      console.warn('[PartialPayment] 🎫 Cihazda ödeme denemesi yapıldı ama satış tamamlanmadı — sipariş numarası yenilenecek');
      onPaymentAttemptFailed();
    }
    onClose(null);
  };

  // Modal her açıldığında deneme işareti sıfırlanır (yeni satış = yeni deneme takibi)
  useEffect(() => {
    if (open) {
      fiscalAttemptMadeRef.current = false;
    }
  }, [open]);

  // ✅ YENİ: Belge süresini güncelle (her saniye)
  useEffect(() => {
    if (!isDocumentPrepared || !liveDocumentStartTime) return;

    const interval = setInterval(() => {
      // Force re-render to update timer
      setLiveDocumentStartTime(prev => prev);
    }, 1000);

    return () => clearInterval(interval);
  }, [isDocumentPrepared, liveDocumentStartTime]);

  // ✅ YENİ: Modal açıldığında yazarkasa bağlantısını kontrol et
  useEffect(() => {
    const checkFiscalConnection = async () => {
      // Modal açık değilse veya yazarkasa zaten atlandıysa kontrol yapma
      if (!open || skipFiscalDevice) return;

      // ⚠️ Rest-P2P modu aktifse eski PavoPay yerel servis (IP'li) bağlantı
      //    kontrolü ANLAMSIZDIR. Rest-P2P kendi bağlantısını SignalR üzerinden
      //    yönetir ve PavoLiveBadge zaten canlı durumu gösterir.
      if (isRestP2PEnabled) {
        console.log('[PartialPayment] ℹ️ Rest-P2P modu aktif - yerel yazarkasa bağlantı kontrolü atlandı');
        return;
      }

      // Kurtarma modunda bağlantı kontrolü atla (checkConnection auto-recover yapar)
      if (ingenicoRecoveryData && selectedFiscalDevice === 'ingenico_5000f') {
        console.log('[PartialPayment] Ingenico kurtarma modu - bağlantı kontrolü atlandı');
        return;
      }
      
      // Yazarkasa seçili değilse kontrol yapma
      if (!selectedFiscalDevice) {
        console.log('[PartialPayment] ℹ️ Yazarkasa seçili değil - kontrol atlanıyor');
        return;
      }

      console.log('[PartialPayment] 🔌 Modal açıldı - Yazarkasa bağlantısı kontrol ediliyor...');
      
      const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
      let fiscalDeviceConnected = false;
      
      try {
        fiscalDeviceConnected = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
        console.log(`[PartialPayment] 🔌 Bağlantı durumu: ${fiscalDeviceConnected ? 'BAĞLI ✅' : 'BAĞLI DEĞİL ❌'}`);
      } catch (connError) {
        console.error('[PartialPayment] 🔌 Bağlantı kontrolü hatası:', connError);
        fiscalDeviceConnected = false;
      }

      // ✅ Yazarkasa BAĞLI DEĞİLSE → Onay modalı göster
      if (!fiscalDeviceConnected) {
        console.log('[PartialPayment] ⚠️ Yazarkasa bağlı değil! Onay modalı gösteriliyor...');
        
        // Callback'i sakla - kullanıcı onaylarsa skipFiscalDevice = true olacak
        setPendingPaymentAction(() => {
          return () => {
            setSkipFiscalDevice(true);
            console.log('[PartialPayment] ✅ Kullanıcı yazarkasasız devam etmeyi onayladı');
          };
        });
        
        // Onay modalını aç
        setIsFiscalDisconnectedConfirmOpen(true);
      }
    };

    checkFiscalConnection();
  }, [open, selectedFiscalDevice, isRestP2PEnabled]);

  // Ingenico kurtarma modu - acik fis ile devam
  useEffect(() => {
    if (!open || !ingenicoRecoveryData || selectedFiscalDevice !== 'ingenico_5000f') return;
    if (isDocumentPrepared) return; // zaten baslatilmis

    console.log('[PartialPayment] Ingenico kurtarma modu aktif edildi:', {
      totalAmount: ingenicoRecoveryData.totalAmountTL,
      totalPayment: ingenicoRecoveryData.totalPaymentTL,
      remaining: ingenicoRecoveryData.remainingAmountTL,
    });

    // Mevcut odemeleri payment history'ye ekle
    const existingPayments: PaymentHistoryItem[] = [];
    if (ingenicoRecoveryData.totalPaymentTL > 0) {
      existingPayments.push({
        id: uuidv4(),
        type: 'cash',
        label: 'Önceki Ödeme (Cihazdan)',
        amount: ingenicoRecoveryData.totalPaymentTL,
        timestamp: Date.now(),
        status: 'paid',
        paymentSequenceNo: 1,
      });
      setPaidByCash(ingenicoRecoveryData.totalPaymentTL);
    }

    setPaymentHistory(existingPayments);
    setPaymentSequenceCounter(existingPayments.length + 1);
    setIsDocumentPrepared(true);
    setIsLiveDocumentMode(true);
    setLiveDocumentStartTime(Date.now());
    setSkipFiscalDevice(false);
    console.log('[PartialPayment] Ingenico kurtarma modu: Belge hazır, ödeme eklenebilir.');
  }, [open, ingenicoRecoveryData, selectedFiscalDevice]);

  // ✅ YENİ: LocalStorage key oluştur
  const getLocalStorageKey = () => {
    if (!companyId || !screenId) return null;
    return `partialPayment_${companyId}_${screenId}`;
  };

  // ✅ YENİ: Kısmi İade LocalStorage key (global - tüm ekranlar için)
  const getPartialRefundStorageKey = () => {
    if (!companyId) return null;
    return `partialRefunds_${companyId}`;
  };

  // ✅ YENİ: Kısmi iade kayıtlarını kaydet
  const savePartialRefundToStorage = (paymentItem: PaymentHistoryItem) => {
    const key = getPartialRefundStorageKey();
    if (!key) return;

    try {
      const existing = localStorage.getItem(key);
      const refunds: PaymentHistoryItem[] = existing ? JSON.parse(existing) : [];
      
      // Aynı ID'ye sahip kayıt varsa güncelle, yoksa ekle
      const existingIndex = refunds.findIndex(r => r.id === paymentItem.id);
      if (existingIndex >= 0) {
        refunds[existingIndex] = paymentItem;
      } else {
        refunds.push(paymentItem);
      }
      
      localStorage.setItem(key, JSON.stringify(refunds));
      console.log('[PartialPayment] Kısmi iade kaydedildi:', paymentItem.label);
    } catch (e) {
      console.error('[PartialPayment] Kısmi iade kaydetme hatası:', e);
    }
  };

  // ✅ YENİ: Kısmi iade kaydını sil
  const removePartialRefundFromStorage = (paymentId: string) => {
    const key = getPartialRefundStorageKey();
    if (!key) return;

    try {
      const existing = localStorage.getItem(key);
      if (!existing) return;
      
      const refunds: PaymentHistoryItem[] = JSON.parse(existing);
      const filtered = refunds.filter(r => r.id !== paymentId);
      
      localStorage.setItem(key, JSON.stringify(filtered));
      console.log('[PartialPayment] Kısmi iade silindi:', paymentId);
    } catch (e) {
      console.error('[PartialPayment] Kısmi iade silme hatası:', e);
    }
  };

  // ✅ YENİ: LocalStorage'dan yükle + Modal kapandığında temizle
  useEffect(() => {
    if (open) {
      const key = getLocalStorageKey();
      if (key) {
        try {
          const saved = localStorage.getItem(key);
          if (saved) {
            const parsed: PaymentHistoryItem[] = JSON.parse(saved);
            setPaymentHistory(parsed);
            
            // Toplamları hesapla
            let cashTotal = 0;
            let posMap: { [posId: string]: number } = {};
            let openAccountTotal = 0;
            
            parsed.forEach(item => {
              if (item.status === 'paid') {
                if (item.type === 'cash') {
                  cashTotal += item.amount;
                } else if (item.type === 'pos' && item.posId) {
                  posMap[item.posId] = (posMap[item.posId] || 0) + item.amount;
                } else if (item.type === 'open_account') {
                  openAccountTotal += item.amount;
                }
              }
            });
            
            setPaidByCash(cashTotal);
            setPaidByPosMap(posMap);
            setPaidByOpenAccount(openAccountTotal);
            
            console.log('[PartialPayment] LocalStorage\'dan yüklendi:', parsed.length, 'ödeme');
          }
        } catch (e) {
          console.error('[PartialPayment] LocalStorage okuma hatası:', e);
        }
      }
      
      setCurrentInput('');
      setActivePaymentType(null);
    } else {
      // ✅ YENİ: Modal kapandığında state'leri temizle (ama localStorage'ı değil)
      setCurrentInput('');
      setPaidByCash(0);
      setPaidByPosMap({});
      setPaidByOpenAccount(0);
      setActivePaymentType(null);
      setPaymentHistory([]);
    }
  }, [open, companyId, screenId]);

  const totalPaid = paidByCash + Object.values(paidByPosMap).reduce((sum, amount) => sum + amount, 0) + paidByOpenAccount;
  
  // ✅ YENİ: Pending nakit, açık hesap VE POS'ları da dahil et
  const pendingCashTotal = paymentHistory
    .filter(h => h.status === 'pending' && h.type === 'cash')
    .reduce((sum, h) => sum + h.amount, 0);
  const pendingOpenAccountTotal = paymentHistory
    .filter(h => h.status === 'pending' && h.type === 'open_account')
    .reduce((sum, h) => sum + h.amount, 0);
  const pendingPosTotal = paymentHistory
    .filter(h => h.status === 'pending' && h.type === 'pos')
    .reduce((sum, h) => sum + h.amount, 0);
  
  const totalPaidIncludingPending = totalPaid + pendingCashTotal + pendingOpenAccountTotal + pendingPosTotal;
  const remainingAmount = totalAmount - totalPaidIncludingPending;
  const changeAmount = totalPaidIncludingPending > totalAmount ? totalPaidIncludingPending - totalAmount : 0;

  // ✅ YENİ: Çoklu POS kontrolü (2+ POS ÖDEMESİ EKLENMİŞSE ödeme al sistemi aktif)
  const posPaymentsCount = paymentHistory.filter(h => h.type === 'pos').length;
  const hasMultiplePosPayments = posPaymentsCount >= 2;

  // ✅ YENİ: Pending POS ödeme kontrolü (sadece çoklu POS eklendiğinde)
  const hasPendingPosPayments = hasMultiplePosPayments && paymentHistory.some(h => h.status === 'pending' && h.type === 'pos');

  // ✅ YENİ: LocalStorage'a kaydet
  const saveToLocalStorage = (history: PaymentHistoryItem[]) => {
    const key = getLocalStorageKey();
    if (key) {
      try {
        localStorage.setItem(key, JSON.stringify(history));
        console.log('[PartialPayment] LocalStorage\'a kaydedildi:', history.length, 'ödeme');
      } catch (e) {
        console.error('[PartialPayment] LocalStorage yazma hatası:', e);
      }
    }
  };

  const handleNumpadInput = (value: string) => {
    if (!activePaymentType) return;

    if (value === 'backspace') {
      setCurrentInput((prev) => prev.slice(0, -1));
    } else if (value === 'C') {
      setCurrentInput('');
    } else if (value === 'Enter') {
      handleSubmitCurrentInput();
    } else if (currentInput.length < 10) {
      if (value === '.' && currentInput.includes('.')) return;
      if (currentInput === '0' && value === '0') return;
      setCurrentInput((prev) => prev + value);
    }
  };

  const handleSubmitCurrentInput = async () => {
    if (!activePaymentType || !currentInput) return;

    const amount = parseFloat(currentInput);
    if (isNaN(amount) || amount <= 0) {
      setCurrentInput('');
      return;
    }

    // 🎯 AKILLI BELGE BAŞLATMA: Kasiyerin niyetini anla
    const fullAmount = parseFloat(remainingAmount.toFixed(2));
    const isFullPayment = Math.abs(amount - fullAmount) < 0.01; // Tam tutar mı?
    
    console.log(`[PartialPayment] 💡 Girilen: ${amount} TL, Kalan: ${fullAmount} TL, Tam Ödeme: ${isFullPayment}`);
    
    // 📌 SENARYO 1: TAM TUTAR → Belge başlatma, direkt ödeme al (hızlı mod)
    if (isFullPayment && !isDocumentPrepared) {
      console.log('[PartialPayment] ✅ TAM TUTAR girdi → Hızlı mod, belge başlatılmadan ödeme alınacak');
      
      // Ödeme geçmişine ekle (pending olarak)
      const newHistoryItem: PaymentHistoryItem = {
        id: uuidv4(),
        type: activePaymentType === 'cash' ? 'cash' : activePaymentType === 'open_account' ? 'open_account' : 'pos',
        label: activePaymentType === 'cash' ? cashOptionLabel : 
               activePaymentType === 'open_account' ? (openAccountOption?.label || 'Açık Hesap') :
               posOptionsList.find(p => p.id === activePaymentType)?.label || 'POS',
        amount,
        timestamp: Date.now(),
        posId: activePaymentType !== 'cash' && activePaymentType !== 'open_account' ? activePaymentType : undefined,
        status: 'pending',
      };

      const updatedHistory = [...paymentHistory, newHistoryItem];
      
      // ⚠️ ÖNEMLİ: State güncellenmeden önce handleCompletePayment'e manuel olarak history gönder
      // Çünkü setPaymentHistory asenkron, ama biz hemen işlem yapacağız
      
      setCurrentInput('');
      setActivePaymentType(null);
      
      // ✅ Direkt "Ödemeyi Tamamla (Hızlı)" mantığını çalıştır
      // Ödeme bilgisini MANUEL olarak hesapla ve gönder
      await handleCompletePaymentWithHistory(updatedHistory);
      
      return;
    }
    
    // 📌 SENARYO 2: KISMI TUTAR → Önce belge başlat, sonra ödeme ekle
    // ✅ skipFiscalDevice flag'i true ise belge hazırlamayı atla
    // ✅ Beko cihazlarda canlı belge modu yok - ödemeler toplanıp processMultiplePayments ile gönderilir
    // ⚠️ Rest-P2P modunda yerel PavoPay canlı belge akışı KULLANILMAZ; tüm ödemeler
    //    sepette biriktirilir, tamamlandığında onClose → usePayment Rest-P2P akışına devreder.
    const isBekoDevice = selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
    if (!isFullPayment && !isDocumentPrepared && !skipFiscalDevice && !isBekoDevice && !isRestP2PEnabled) {
      console.log('[PartialPayment] ⚠️ KISMI TUTAR girdi → Önce belge başlatılacak');
      
      // Önce belgeyi hazırla
      await handlePrepareDocument();
      
      // Belge hazırlandı mı kontrol et
      // NOT: handlePrepareDocument state'i güncelleyecek, ama henüz güncellenmemiş olabilir
      // Bu yüzden bir sonraki adımda ödeme eklenecek
    }

    // ✅ Ödeme geçmişine ekle
    const newHistoryItem: PaymentHistoryItem = {
      id: uuidv4(),
      type: activePaymentType === 'cash' ? 'cash' : activePaymentType === 'open_account' ? 'open_account' : 'pos',
      label: activePaymentType === 'cash' ? cashOptionLabel : 
             activePaymentType === 'open_account' ? (openAccountOption?.label || 'Açık Hesap') :
             posOptionsList.find(p => p.id === activePaymentType)?.label || 'POS',
      amount,
      timestamp: Date.now(),
      posId: activePaymentType !== 'cash' && activePaymentType !== 'open_account' ? activePaymentType : undefined,
      status: 'pending',
    };

    const updatedHistory = [...paymentHistory, newHistoryItem];
    setPaymentHistory(updatedHistory);
    saveToLocalStorage(updatedHistory);
    
    setCurrentInput('');
    setActivePaymentType(null);
  };
  
  const handleSelectPaymentType = async (type: string) => {
    // ✅ AKILLI BELGE BAŞLATMA: Kasiyerin davranışına göre karar ver
    
    // 1️⃣ Kalan tutar kontrolü
    const fullAmount = parseFloat(remainingAmount.toFixed(2));
    
    if (fullAmount <= 0) {
      showAlertModal?.('Uyarı', 'Kalan tutar 0 veya daha az!');
      return;
    }
    
    // 2️⃣ Belge zaten hazırsa → Normal akış (rakam girişi aç)
    if (isDocumentPrepared) {
      setCurrentInput('');
      setActivePaymentType(type);
      return;
    }
    
    // 3️⃣ Belge hazır değil → Rakam girişi aç (kasiyer kısmi tutar girecek)
    setCurrentInput('');
    setActivePaymentType(type);
  };

  // ✅ YENİ: Silme onayı aç
  const handleDeleteClick = (itemId: string) => {
    setItemToDelete(itemId);
    setIsDeleteConfirmOpen(true);
  };

  // ✅ YENİ: Silme onaylandı
  const handleDeleteConfirm = () => {
    if (!itemToDelete) return;
    
    const updatedHistory = paymentHistory.filter(h => h.id !== itemToDelete);
    setPaymentHistory(updatedHistory);
    saveToLocalStorage(updatedHistory);
    
    setIsDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  // ✅ YENİ: Yeniden Dene fonksiyonu
  const handleRetry = async () => {
    console.log('[PartialPayment] Yeniden deneme başlıyor...');
    
    // Tüm adımları temizle - yeni bir deneme olacak
    setPaymentSteps([]);
    setCurrentFailedStep(null);
    setIsProcessingSteps(false);
    
    // Kısa bir gecikme ekle (UI güncellemesi için)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Ödeme işlemini tekrar başlat
    await handleCompletePayment();
  };

  // ✅ YENİ: "Canlı Belge" Modu - Belgeyi Hazırla
  const handlePrepareDocument = async (forceSkipFiscal: boolean = false) => {
    try {
      console.log('[PartialPayment] 🟢 CANLI BELGE MODU: Belge hazırlanıyor...');
      console.log('[PartialPayment] 🔌 forceSkipFiscal:', forceSkipFiscal, 'skipFiscalDevice:', skipFiscalDevice);

      // ⚠️ Rest-P2P modunda yerel PavoPay canlı belge akışı (StartSaleWithItems vb.)
      //    HİÇ başlatılmaz. Pavo tarafı belgeyi kendisi yönetir; web sadece
      //    ödeme özetini toplayıp onClose ile Rest-P2P akışına devreder.
      if (isRestP2PEnabled) {
        console.log('[PartialPayment] ℹ️ Rest-P2P modu aktif - yerel belge hazırlama atlandı');
        return;
      }
      
      // ✅ YENİ: Yazarkasa bağlantı kontrolü (forceSkipFiscal veya skipFiscalDevice flag'i yoksa)
      // ⚠️ Rest-P2P modunda yerel servis bağlantı kontrolü atlanır (SignalR ile yönetilir)
      if (!forceSkipFiscal && !skipFiscalDevice && !isRestP2PEnabled) {
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        let fiscalDeviceConnected = false;
        
        try {
          console.log('[PartialPayment] 🔌 Yazarkasa bağlantısı kontrol ediliyor...');
          fiscalDeviceConnected = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
          console.log(`[PartialPayment] 🔌 Bağlantı durumu: ${fiscalDeviceConnected ? 'BAĞLI ✅' : 'BAĞLI DEĞİL ❌'}`);
        } catch (connError) {
          console.error('[PartialPayment] 🔌 Bağlantı kontrolü hatası:', connError);
          fiscalDeviceConnected = false;
        }

        // ✅ Yazarkasa BAĞLI DEĞİLSE → Onay modalı göster
        if (!fiscalDeviceConnected) {
          console.log('[PartialPayment] ⚠️ Yazarkasa bağlı değil! Onay modalı gösteriliyor...');
          
          // Callback'i sakla (kullanıcı onaylarsa çağrılacak)
          setPendingPaymentAction(() => {
            return () => {
              setSkipFiscalDevice(true); // Flag'i ayarla
              handlePrepareDocument(true); // forceSkipFiscal = true
            };
          });
          
          // Onay modalını aç
          setIsFiscalDisconnectedConfirmOpen(true);
          return;
        }
      }

      // ✅ Yazarkasa yoksa veya atlanacaksa, belgeyi hazırlamadan devam et
      // Bu durumda parçalı ödeme yerine hızlı ödeme modu kullanılacak
      if (forceSkipFiscal || skipFiscalDevice) {
        console.log('[PartialPayment] ⏭️ Yazarkasa atlandı - Belge hazırlanmadan, hızlı mod aktif');
        // isDocumentPrepared = false kalacak, bu sayede handleCompletePaymentWithHistory çağrılacak
        return;
      }
      
      // ✅ PavoPay için özel akış
      if (selectedFiscalDevice === 'pavopay') {
        console.log('[PartialPayment] 🔵 PavoPay parçalı ödeme başlatılıyor...', { isRestaurantMode, totalAmount });
        
        // Ürünleri PavoPay formatına dönüştür
        // Parçalı ödemede fiyatlar sepetteki gibi (KDV dahil) gönderilmeli
        const pavoPayProducts = cart.map(cartItem => {
          const inventoryItem = inventoryItems.find(inv => inv.id === cartItem.id);
          const vatRate = cartItem.vatRate || inventoryItem?.vatRate || 0;
          
          console.log(`[PartialPayment-PAVOPAY] ${cartItem.name}: ${cartItem.price.toFixed(2)} TL (sepet fiyatı, olduğu gibi)`);
          
          return {
            name: cartItem.name,
            quantity: cartItem.quantity,
            unitPrice: cartItem.price,
            vatRate: vatRate,
            departmentNo: inventoryItem?.departmentNo || 1,
          };
        });
        
        // Fiyatlar sepetten KDV dahil geliyor, PavoPay'e de KDV dahil olarak gönder
        const result = await pavoPayStartSale(pavoPayProducts, totalAmount, companyId || '', true, kitchenOrderCode, receiptCompanyName, useExternalPrinterForPavo);
        
        if (!result.success || !result.saleNumber) {
          showAlertModal('PavoPay Hatası', result.errorMessage || 'Satış başlatılamadı');
          return;
        }
        
        // Satış numarasını ve PavoPay'in kabul ettiği GrossPrice'ı sakla
        setPavoPaySaleNumber(result.saleNumber);
        setPavoPayGrossPrice(result.totalAmount || totalAmount);
        setIsDocumentPrepared(true);
        setIsLiveDocumentMode(true);
        setLiveDocumentStartTime(Date.now());
        
        console.log(`[PartialPayment] ✅ PavoPay satışı başlatıldı: ${result.saleNumber}, GrossPrice: ${result.totalAmount || totalAmount}`);
        return;
      }
      
      // ⚠️ Diğer cihazlar için mevcut akış
      const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
      
      if (!fiscalDevice.startDocumentWithItems) {
        throw new Error('Canlı belge modu desteklenmiyor');
      }

      // Ürünleri hazırla - ⚠️ KDV kontrolü eklendi
      const fiscalProducts = cart.map(cartItem => {
        const inventoryItem = inventoryItems.find(inv => inv.id === cartItem.id);
        const vatRate = cartItem.vatRate || inventoryItem?.vatRate || 0;
        
        // ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat, 'excluded' = KDV hariç fiyat
        const isVatIncluded = cartItem.vatStatus === 'included' || isRestaurantMode;
        
        // ✅ Inpos/Hugin KDV HARİÇ fiyat bekliyor
        let fiscalPrice: number;
        if (isVatIncluded) {
          // Fiyat KDV dahil geliyor, KDV'den arındır (yazarkasa tekrar ekleyecek)
          fiscalPrice = cartItem.price / (1 + vatRate / 100);
          console.log(`[PartialPayment-BELGE] ${cartItem.name}: ${cartItem.price.toFixed(2)} TL (KDV dahil) → ${fiscalPrice.toFixed(2)} TL (KDV hariç)`);
        } else {
          // Fiyat zaten KDV hariç, olduğu gibi gönder
          fiscalPrice = cartItem.price;
          console.log(`[PartialPayment-BELGE] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV hariç, olduğu gibi)`);
        }

        return {
          id: cartItem.id,
          name: cartItem.name,
          quantity: cartItem.quantity,
          price: fiscalPrice,
          vatRate: vatRate,
          departmentNo: inventoryItem?.departmentNo || 1,
        };
      });

      // Belgeyi başlat ve ürünleri ekle (totalAmount ile yuvarlama düzeltmesi)
      const result = await fiscalDevice.startDocumentWithItems(fiscalProducts, showAlertModal, totalAmount);

      if (!result.success) {
        showAlertModal('Belge Hazırlama Hatası', result.errorMessage || 'Belge hazırlanamadı');
        return;
      }

      // Başarılı - Canlı belge modunu aktif et
      setIsDocumentPrepared(true);
      setIsLiveDocumentMode(true);
      setLiveDocumentStartTime(Date.now());
      
      console.log('[PartialPayment] ✅ Belge hazırlandı. Ödemeler eklenebilir.');
      
    } catch (error: any) {
      console.error('[PartialPayment] Belge hazırlama hatası:', error);
      showAlertModal('Hata', error.message || 'Belge hazırlanırken bir hata oluştu');
    }
  };

  // ✅ YENİ: "Canlı Belge" Modu - Tek Ödeme Ekle
  // ✅ Başarılı ödemeyi 'paid' işaretle, toplamları güncelle ve gerekiyorsa belgeyi kapat.
  // handleAddPayment'in normal başarı yolundan ve Ingenico "Yine de Devam Et"
  // onayından ortak olarak çağrılır.
  const finalizeSuccessfulPayment = async (paymentItem: PaymentHistoryItem, fiscalData?: any) => {
    // Başarılı - Ödemeyi 'paid' olarak işaretle ve paymentSequenceNo kaydet
    const currentSequenceNo = paymentSequenceCounter;
    setPaymentSequenceCounter(prev => prev + 1);

    const updatedHistory = paymentHistory.map(item => {
      if (item.id === paymentItem.id) {
        return {
          ...item,
          status: 'paid' as const,
          fiscalData,
          paymentSequenceNo: currentSequenceNo, // ✅ Sıra numarasını kaydet
        };
      }
      return item;
    });

    setPaymentHistory(updatedHistory);
    saveToLocalStorage(updatedHistory);

    // Toplamları güncelle
    if (paymentItem.type === 'cash') {
      setPaidByCash(prev => prev + paymentItem.amount);
    } else if (paymentItem.type === 'pos' && paymentItem.posId) {
      setPaidByPosMap(prev => ({
        ...prev,
        [paymentItem.posId!]: (prev[paymentItem.posId!] || 0) + paymentItem.amount,
      }));
    }

    console.log(`[PartialPayment] ✅ Ödeme başarıyla eklendi: ${paymentItem.label}`);

    // ✅ PavoPay: Son ödeme yapıldıysa (AddPaymentAndFinalizeSale) satış zaten otomatik kapandı
    // NOT: PavoPay'de kalan tutar 0 olduğunda belge otomatik kapanır, "Belgeyi Kapat" butonuna gerek yok
    const isPavoPayFinalPayment = selectedFiscalDevice === 'pavopay' &&
      paymentItem.amount >= (totalAmount - (paidByCash + Object.values(paidByPosMap).reduce((sum, a) => sum + a, 0) + paidByOpenAccount)) - 0.01;

    if (selectedFiscalDevice === 'pavopay' && isPavoPayFinalPayment) {
      console.log('[PartialPayment] 🔒 PavoPay satışı otomatik kapandı (kalan tutar 0), modal kapatılıyor...');

      // Fiscal data'yı sakla
      setLastFiscalData(fiscalData || {});

      // Toplamları hesapla
      const finalTotalPaid = paidByCash + paymentItem.amount + Object.values(paidByPosMap).reduce((sum, amount) => sum + amount, 0) + paidByOpenAccount;

      const paymentDetails: PaymentDetails = {
        paidByCash: paidByCash + (paymentItem.type === 'cash' ? paymentItem.amount : 0),
        paidByPosMap: paymentItem.type === 'pos' && paymentItem.posId
          ? { ...paidByPosMap, [paymentItem.posId]: (paidByPosMap[paymentItem.posId] || 0) + paymentItem.amount }
          : paidByPosMap,
        paidByOpenAccount,
        totalPaid: finalTotalPaid,
        remainingAmount: totalAmount - finalTotalPaid,
        changeAmount: finalTotalPaid > totalAmount ? finalTotalPaid - totalAmount : 0,
        fiscalProcessed: true,
        fiscalData,
      };

      // LocalStorage'ı temizle
      const key = getLocalStorageKey();
      if (key) {
        localStorage.removeItem(key);
        console.log('[PartialPayment] LocalStorage temizlendi');
      }

      // State'leri temizle
      setIsDocumentPrepared(false);
      setIsLiveDocumentMode(false);
      setLiveDocumentStartTime(null);
      setLastFiscalData(null);
      setPaymentSequenceCounter(1);
      setProcessingPaymentId(null);
      setPavoPaySaleNumber(null);
      setPavoPayGrossPrice(null);

      onClose(paymentDetails);
      return;
    }

    // Kalan tutar 0 mı kontrol et (PavoPay olmayan cihazlar için)
    const newTotalPaid = paidByCash + paymentItem.amount + (paymentItem.type === 'pos' && paymentItem.posId ? (paidByPosMap[paymentItem.posId] || 0) : 0) + Object.values(paidByPosMap).reduce((sum, amount) => sum + amount, 0);
    const newRemainingAmount = totalAmount - newTotalPaid;

    if (Math.abs(newRemainingAmount) < 0.001 && selectedFiscalDevice !== 'pavopay') {
      // Kalan tutar 0 - Belgeyi otomatik kapat (PavoPay hariç - zaten kapandı)
      console.log('[PartialPayment] 🔒 Kalan tutar 0, belge otomatik kapatılıyor...');

      // ✅ FIX: React state henüz güncellenmemiş olabilir (setPaidByPosMap/setPaidByCash async)
      // Bu yüzden updatedHistory'den (local variable, güncel) doğru toplamları hesapla
      let computedCash = 0;
      const computedPosMap: { [key: string]: number } = {};
      let computedOpenAccount = 0;

      updatedHistory.forEach(h => {
        if (h.status === 'paid') {
          if (h.type === 'cash') computedCash += h.amount;
          else if (h.type === 'pos' && h.posId) {
            computedPosMap[h.posId] = (computedPosMap[h.posId] || 0) + h.amount;
          }
          else if (h.type === 'open_account') computedOpenAccount += h.amount;
        }
      });

      console.log(`[PartialPayment] 📊 Hesaplanan toplamlar - Nakit: ${computedCash}, POS: ${JSON.stringify(computedPosMap)}, Açık Hesap: ${computedOpenAccount}`);

      await handleCloseDocument({ paidByCash: computedCash, paidByPosMap: computedPosMap, paidByOpenAccount: computedOpenAccount });
    }
  };

  // ✅ YENİ (Ingenico): "Yine de Devam Et" onayı — kullanıcı ödemenin cihazdan
  // alındığını biliyor (POS slibi/cihaz ekranı), sistem doğrulayamadı. Ödeme
  // alınmış sayılır ve akış normal şekilde devam eder.
  const handleContinueFailedPayment = async () => {
    const paymentItem = failedPaymentForContinue;
    setFailedPaymentForContinue(null);
    setFailedPaymentErrorMsg('');
    if (!paymentItem) return;
    console.warn(`[PartialPayment] 👤 Kullanıcı "Yine de Devam Et" dedi: ${paymentItem.label} - ${paymentItem.amount} TL alınmış sayılıyor.`);
    try {
      await finalizeSuccessfulPayment(paymentItem, { source: 'continue_anyway_user_confirmed' });
    } catch (err: any) {
      console.error('[PartialPayment] Yine de devam et hatası:', err);
      showAlertModal('Hata', err.message || 'Ödeme kaydedilirken bir hata oluştu');
    }
  };

  const handleAddPayment = async (paymentItem: PaymentHistoryItem) => {
    // ⚠️ Rest-P2P modunda canlı belgeye tek tek ödeme eklenmez;
    //    tüm ödemeler sepette biriktirilir, sonunda Pavo'ya mixed akışla gönderilir.
    if (isRestP2PEnabled) {
      console.log('[PartialPayment] ℹ️ Rest-P2P modu - tekil canlı belge ödeme çağrısı atlandı');
      return;
    }

    // ✅ Artık "Belgeyi Hazırla" butonuna gerek yok - otomatik başlıyor
    if (!isDocumentPrepared) {
      showAlertModal('Uyarı', 'Belge henüz hazırlanmadı. Lütfen bekleyiniz...');
      return;
    }

    try {
      console.log(`[PartialPayment] 💳 Ödeme ekleniyor: ${paymentItem.label} - ${paymentItem.amount} TL`);
      
      setProcessingPaymentId(paymentItem.id);
      
      // ✅ YENİ: POS ödemesi ise mavi "Ödeme Bekleniyor" modalını aç
      // ⚠️ Rest-P2P modunda PavoRestP2PWaitModal gösterilir; ikinci bir mavi
      //    backdrop AÇILMAMALI (çakışma yaratır).
      if (paymentItem.type === 'pos' && !isRestP2PEnabled) {
        setIsProcessingPosPayment(true);
      }
      
      let result: { success: boolean; errorMessage?: string; fiscalData?: any; saleNumber?: string; invoiceNo?: string } = { success: false };
      
      // ✅ PavoPay için özel akış (pavopay-test.html mantığı)
      if (selectedFiscalDevice === 'pavopay' && pavoPaySaleNumber) {
        // ✅ pavopay-test.html'den: Kalan tutarı hesapla
        const currentPaidAmount = paidByCash + Object.values(paidByPosMap).reduce((sum, a) => sum + a, 0) + paidByOpenAccount;
        const remainingBeforePayment = totalAmount - currentPaidAmount;
        
        // ✅ Son ödeme mi kontrol et (pavopay-test.html: amount >= remainingBefore)
        const isFinalPayment = paymentItem.amount >= remainingBeforePayment - 0.01; // 0.01 tolerans
        
        console.log(`[PartialPayment] 🔵 PavoPay Ödeme:`, {
          amount: paymentItem.amount,
          remainingBefore: remainingBeforePayment,
          isFinalPayment,
          mediator: paymentItem.type === 'cash' ? 1 : 2,
          endpoint: isFinalPayment ? 'AddPaymentAndFinalizeSale' : 'AddPayment'
        });
        
        // PavoPay'in StartSaleWithItems'da kabul ettiği GrossPrice'ı kullan
        const pavoGross = pavoPayGrossPrice || totalAmount;
        
        if (isFinalPayment) {
          // ✅ Son ödeme → AddPaymentAndFinalizeSale (ödeme + satış kapama tek seferde)
          console.log(`[PartialPayment] 🔵 PavoPay AddPaymentAndFinalizeSale (Son Ödeme), GrossPrice: ${pavoGross}`);
          
          markFiscalAttempt();
          const pavoResult = await pavoPayAddPaymentAndFinalize(
            pavoPaySaleNumber,
            paymentItem.amount,
            // Nakit sabit 1; kart bacağı ekran ayarındaki aracıya gider (varsayılan 2 = BKM/TechPOS)
            paymentItem.type === 'cash' ? 1 : getPavoCardMediator(),
            companyId || '',
            pavoGross,
            cart.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
            kitchenOrderCode,
            receiptCompanyName,
            useExternalPrinterForPavo
          );
          
          result = {
            success: pavoResult.success,
            errorMessage: pavoResult.errorMessage,
            fiscalData: pavoResult.data,
            saleNumber: pavoResult.saleNumber,
            invoiceNo: pavoResult.invoiceNo
          };
          
          if (pavoResult.success) {
            console.log(`[PartialPayment] ✅ PavoPay satışı otomatik kapandı: ${pavoResult.saleNumber}`);
          }
        } else {
          // ✅ Kısmi ödeme → AddPayment (sadece ödeme ekle)
          console.log(`[PartialPayment] 🔵 PavoPay AddPayment (Kısmi Ödeme), GrossPrice: ${pavoGross}`);
          
          markFiscalAttempt();
          const pavoResult = await pavoPayAddPayment(
            pavoPaySaleNumber,
            paymentItem.amount,
            // Nakit sabit 1; kart bacağı ekran ayarındaki aracıya gider (varsayılan 2 = BKM/TechPOS)
            paymentItem.type === 'cash' ? 1 : getPavoCardMediator(),
            pavoGross
          );
          
          result = {
            success: pavoResult.success,
            errorMessage: pavoResult.errorMessage,
            fiscalData: pavoResult.data
          };
        }
      } else {
        // ⚠️ Diğer cihazlar için mevcut akış
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        if (!fiscalDevice.addSinglePayment) {
          throw new Error('Tek ödeme ekleme desteklenmiyor');
        }

        // Ödemeyi ekle
        markFiscalAttempt();
        result = await fiscalDevice.addSinglePayment(
          paymentItem.type === 'cash' ? 'cash' : 'pos',
          paymentItem.amount,
          showAlertModal
        );
      }
      
      // ✅ YENİ: POS ödemesi tamamlandı - mavi modalı kapat
      if (paymentItem.type === 'pos') {
        setIsProcessingPosPayment(false);
      }
      
      setProcessingPaymentId(null);

      if (!result.success) {
        // ✅ YENİ (Ingenico): GetTicket doğrulaması — hata dönmüş olsa bile ödeme
        // cihaza gerçekte işlenmiş olabilir. local-service hata yanıtına cihazdaki
        // gerçek toplam ödemeyi (deviceState) ekler; beklenen toplamla karşılaştır.
        if (selectedFiscalDevice === 'ingenico_5000f') {
          const ds = (result as any).deviceState as
            | { verified: boolean; totalPaymentTL?: number }
            | undefined;
          const paidSoFar = paidByCash + Object.values(paidByPosMap).reduce((s, a) => s + a, 0) + paidByOpenAccount;
          const expectedTotalPaid = paidSoFar + paymentItem.amount;

          if (ds?.verified && typeof ds.totalPaymentTL === 'number' && ds.totalPaymentTL >= expectedTotalPaid - 0.005) {
            console.warn(`[PartialPayment] ✅ GetTicket doğrulaması: ödeme cihazda KAYITLI (cihaz: ${ds.totalPaymentTL} TL >= beklenen: ${expectedTotalPaid} TL). Başarılı sayılıyor.`);
            await finalizeSuccessfulPayment(paymentItem, result.fiscalData || { source: 'get_ticket_verified' });
            return;
          }

          if (ds?.verified) {
            // Cihaz durumu okundu ve ödeme cihaza İŞLENMEMİŞ → kesin başarısız
            showAlertModal(
              'Ödeme Hatası',
              (result.errorMessage || 'Ödeme eklenemedi') +
              '\n\nCihaz kontrol edildi: bu ödeme cihaza İŞLENMEMİŞ. Güvenle tekrar deneyebilirsiniz.'
            );
            return;
          }

          // Cihaz durumu DOĞRULANAMADI → "Yine de Devam Et" seçeneği sun
          setFailedPaymentForContinue(paymentItem);
          setFailedPaymentErrorMsg(result.errorMessage || 'Ödeme eklenemedi');
          return;
        }

        showAlertModal('Ödeme Hatası', result.errorMessage || 'Ödeme eklenemedi');
        return;
      }

      await finalizeSuccessfulPayment(paymentItem, result.fiscalData);
      
    } catch (error: any) {
      // ✅ YENİ: Hata durumunda da mavi modalı kapat
      if (paymentItem.type === 'pos') {
        setIsProcessingPosPayment(false);
      }
      
      setProcessingPaymentId(null);
      console.error('[PartialPayment] Ödeme ekleme hatası:', error);
      
      // ✅ YENİ (Ingenico): Beklenmeyen hatada da "Yine de Devam Et" seçeneği sun
      if (selectedFiscalDevice === 'ingenico_5000f') {
        setFailedPaymentForContinue(paymentItem);
        setFailedPaymentErrorMsg(error.message || 'Ödeme eklenirken bir hata oluştu');
        return;
      }
      
      showAlertModal('Hata', error.message || 'Ödeme eklenirken bir hata oluştu');
    }
  };

  // ✅ YENİ: "Canlı Belge" Modu - Belgeyi Kapat
  // ✅ FIX: overridePaymentData parametresi eklendi - React state async güncellemesi nedeniyle
  // aynı execution context'te çağrıldığında stale state okumasını önler
  const handleCloseDocument = async (overridePaymentData?: { paidByCash: number; paidByPosMap: { [key: string]: number }; paidByOpenAccount: number }) => {
    try {
      console.log('[PartialPayment] 🔒 Belge kapatılıyor...');
      
      let result: { success: boolean; errorMessage?: string; fiscalData?: any; saleNumber?: string; invoiceNo?: string } = { success: false };
      
      // ✅ PavoPay için özel akış
      if (selectedFiscalDevice === 'pavopay' && pavoPaySaleNumber) {
        console.log(`[PartialPayment] 🔵 PavoPay FinalizeSale: ${pavoPaySaleNumber}`);
        
        const pavoResult = await finalizePavoPaySale(
          pavoPaySaleNumber,
          companyId || '',
          pavoPayGrossPrice || totalAmount,
          cart.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
          kitchenOrderCode,
          receiptCompanyName,
          useExternalPrinterForPavo
        );
        
        result = {
          success: pavoResult.success,
          errorMessage: pavoResult.errorMessage,
          fiscalData: pavoResult.data,
          saleNumber: pavoResult.saleNumber,
          invoiceNo: pavoResult.invoiceNo
        };
        
        // ✅ Başarılı satış sonrası localStorage'a kaydet
        if (pavoResult.success && pavoResult.saleNumber) {
          savePavoPaySale({
            saleNumber: pavoResult.saleNumber,
            invoiceNo: pavoResult.invoiceNo || '',
            amount: totalAmount,
            date: new Date().toISOString(),
            paymentType: 'KARMA', // Parçalı ödeme
            items: cart.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
            fiscalData: pavoResult.data
          }, companyId || '');
        }
      } else {
        // ⚠️ Diğer cihazlar için mevcut akış
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        if (!fiscalDevice.closeDocument) {
          throw new Error('Belge kapatma desteklenmiyor');
        }

        result = await fiscalDevice.closeDocument(showAlertModal);
      }

      if (!result.success) {
        showAlertModal('Belge Kapatma Hatası', result.errorMessage || 'Belge kapatılamadı');
        return;
      }

      console.log('[PartialPayment] ✅ Belge başarıyla kapatıldı');
      
      // Fiscal data'yı sakla
      setLastFiscalData(result.fiscalData || {});

      // ✅ FIX: React state güncellemeleri async olduğu için, override verisi varsa onu kullan
      // Bu, aynı execution context'te çağrıldığında (auto-close) stale state okumasını önler
      const finalPaidByCash = overridePaymentData?.paidByCash ?? paidByCash;
      const finalPaidByPosMap = overridePaymentData?.paidByPosMap ?? paidByPosMap;
      const finalPaidByOpenAccount = overridePaymentData?.paidByOpenAccount ?? paidByOpenAccount;

      const finalTotalPaid = finalPaidByCash + Object.values(finalPaidByPosMap).reduce((sum, amount) => sum + amount, 0) + finalPaidByOpenAccount;

      const paymentDetails: PaymentDetails = {
        paidByCash: finalPaidByCash,
        paidByPosMap: finalPaidByPosMap,
        paidByOpenAccount: finalPaidByOpenAccount,
        totalPaid: finalTotalPaid,
        remainingAmount: totalAmount - finalTotalPaid,
        changeAmount: finalTotalPaid > totalAmount ? finalTotalPaid - totalAmount : 0,
        fiscalProcessed: true,
        fiscalData: lastFiscalData,
      };

      // LocalStorage'ı temizle
      const key = getLocalStorageKey();
      if (key) {
        localStorage.removeItem(key);
        console.log('[PartialPayment] LocalStorage temizlendi');
      }
      
      // State'leri temizle
      setIsDocumentPrepared(false);
      setIsLiveDocumentMode(false);
      setLiveDocumentStartTime(null);
      setLastFiscalData(null);
      setPaymentSequenceCounter(1);
      setProcessingPaymentId(null);
      setPavoPaySaleNumber(null);
      setPavoPayGrossPrice(null);

      // ✅ ÖNCE modal'ı kapat ve işlemi tamamla
      onClose(paymentDetails);
      
      // ✅ SONRA kısmi iade edilen POS ödemeleri varsa gerçek iade modalını aç
      // Ingenico 5000F icin VoidPayment zaten gercek POS iadesini yapiyor, ekstra modal gereksiz
      if (selectedFiscalDevice !== 'ingenico_5000f') {
        const partiallyRefundedPosPayments = paymentHistory.filter(
          p => p.type === 'pos' && p.status === 'refunded'
        );
        
        if (partiallyRefundedPosPayments.length > 0) {
          console.log(`[PartialPayment] ⚠️ ${partiallyRefundedPosPayments.length} adet kısmi iade edilen POS ödemesi var, LocalStorage'a kaydediliyor...`);
          
          partiallyRefundedPosPayments.forEach(payment => {
            savePartialRefundToStorage({
              ...payment,
              status: 'refunded',
            });
          });
          
          setRefundQueue(partiallyRefundedPosPayments);
          setCurrentRefundIndex(0);
          setRefundResults([]);
          setIsRealRefundModalOpen(true);
        }
      } else {
        console.log('[PartialPayment] ✅ Ingenico 5000F: VoidPayment ile iade zaten yapıldı, ekstra modal gerekmiyor.');
      }
      
    } catch (error: any) {
      // setIsProcessingSteps(false); // ❌ KALDIRILDI
      console.error('[PartialPayment] Belge kapatma hatası:', error);
      showAlertModal('Hata', error.message || 'Belge kapatılırken bir hata oluştu');
    }
  };

  // ✅ YENİ: "Canlı Belge" Modu - Açık belgeyi iptal et
  const handleCancelLiveDocument = async () => {
    try {
      console.log('[PartialPayment] ❌ Canlı belge iptal ediliyor...');
      
      // ⚠️ ÖNEMLİ: Hem 'paid' hem de 'refunded' (kısmi iade) POS ödemelerini topla
      const paidPosPayments = paymentHistory.filter(
        p => p.type === 'pos' && (p.status === 'paid' || p.status === 'refunded')
      );
      
      let result: { success: boolean; errorMessage?: string } = { success: false };
      let deviceWasReset = false;
      
      // ✅ PavoPay için özel akış
      if (selectedFiscalDevice === 'pavopay' && pavoPaySaleNumber) {
        console.log(`[PartialPayment] 🔵 PavoPay AbandonSuspendedSale: ${pavoPaySaleNumber}`);
        
        result = await abandonPavoPaySale(pavoPaySaleNumber);
      } else if (selectedFiscalDevice === 'ingenico_5000f') {
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        console.log('[PartialPayment] 🔍 Ingenico cihaz durumu kontrol ediliyor...');
        try {
          const doEcho = async () => {
            const echoResponse = await fetch('http://localhost:3003/api/yazarkasa/gmp3-echo', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({}),
              signal: AbortSignal.timeout(15000),
            });
            return await echoResponse.json();
          };
          
          let echoData = await doEcho();
          
          // Echo başarısızsa eşleştirme düşmüş olabilir (cihaz resetlenmiş DEĞİL).
          // Cihazda hâlâ açık fiş duruyor olabilir; yerel state'i temizlemeden önce
          // yeniden bağlanma + eşleştirme dene ve Echo'yu tekrarla.
          if (!echoData.success) {
            console.warn('[PartialPayment] ⚠️ Ingenico echo başarısız, yeniden bağlanma deneniyor...');
            const reconnected = await attemptIngenicoReconnect();
            if (reconnected) {
              echoData = await doEcho();
            }
          }
          
          if (echoData.success) {
            const responseObj = echoData.response
              ? (typeof echoData.response === 'string' ? JSON.parse(echoData.response) : echoData.response)
              : echoData;
            
            const hasActiveHandle = responseObj?.Handle && responseObj.Handle !== '0' && responseObj.Handle !== 0;
            
            if (!hasActiveHandle) {
              console.log('[PartialPayment] ✅ Ingenico cihaz resetlenmiş / açık işlem yok. Lokal state temizleniyor.');
              deviceWasReset = true;
              result = { success: true };
            } else {
              console.log(`[PartialPayment] 📋 Ingenico cihazda açık işlem var (Handle: ${responseObj.Handle}). Normal iptal yapılıyor...`);
              result = await fiscalDevice.cancelDocument!(showAlertModal);
            }
          } else {
            console.warn('[PartialPayment] ⚠️ Ingenico echo başarısız, bağlantı kopmuş olabilir. Lokal state temizleniyor.');
            deviceWasReset = true;
            result = { success: true };
          }
        } catch (echoError: any) {
          console.warn('[PartialPayment] ⚠️ Ingenico bağlantı hatası:', echoError.message);
          deviceWasReset = true;
          result = { success: true };
        }
      } else {
        // ⚠️ Diğer cihazlar için mevcut akış
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        if (!fiscalDevice.cancelDocument) {
          throw new Error('Belge iptal etme desteklenmiyor');
        }

        console.log('[PartialPayment] 1️⃣ Belge iptal ediliyor...');
        result = await fiscalDevice.cancelDocument(showAlertModal);
      }
      
      if (!result.success) {
        showAlertModal('Belge İptal Hatası', result.errorMessage || 'Belge iptal edilemedi');
        return;
      }

      console.log('[PartialPayment] ✅ Belge başarıyla iptal edildi');
      
      // setIsProcessingSteps(false); // ❌ KALDIRILDI
      
      // State'leri temizle
      setIsDocumentPrepared(false);
      setIsLiveDocumentMode(false);
      setLiveDocumentStartTime(null);
      setPaymentSequenceCounter(1);
      setProcessingPaymentId(null);
      setPavoPaySaleNumber(null);
      setPavoPayGrossPrice(null);
      
      // Ödemeleri temizle
      setPaymentHistory([]);
      setPaidByCash(0);
      setPaidByPosMap({});
      setPaidByOpenAccount(0);
      
      // LocalStorage'ı temizle
      const key = getLocalStorageKey();
      if (key) {
        localStorage.removeItem(key);
        console.log('[PartialPayment] LocalStorage temizlendi');
      }
      
      // ✅ ÖNCE modalı kapat (deneme yapıldıysa numara yenilemesi tetiklenir)
      closeWithoutSuccess();
      
      if (deviceWasReset) {
        showAlertModal('✅ İptal Edildi', 'Cihaz resetlenmiş veya bağlantı kopmuş. İşlem lokal olarak temizlendi. Yeni bir işlem başlatabilirsiniz.');
      } else {
        showAlertModal('✅ İptal Edildi', 'Belge başarıyla iptal edildi. Yeni bir işlem başlatabilirsiniz.');
      }
      
      // ✅ SONRA POS ödemeleri varsa gerçek iade modalını aç
      // Ingenico 5000F icin VoidAll + Close zaten tum odemeleri (POS dahil) iptal eder
      if (paidPosPayments.length > 0 && selectedFiscalDevice !== 'ingenico_5000f') {
        console.log(`[PartialPayment] 2️⃣ ${paidPosPayments.length} adet POS ödemesi için LocalStorage'a kaydediliyor...`);
        
        paidPosPayments.forEach(payment => {
          savePartialRefundToStorage({
            ...payment,
            status: 'refunded',
          });
        });
        
        setRefundQueue(paidPosPayments);
        setCurrentRefundIndex(0);
        setRefundResults([]);
        setIsRealRefundModalOpen(true);
      } else if (paidPosPayments.length > 0 && selectedFiscalDevice === 'ingenico_5000f') {
        console.log('[PartialPayment] ✅ Ingenico 5000F: VoidAll ile tüm ödemeler (POS dahil) zaten iptal edildi.');
      }
      
    } catch (error: any) {
      // setIsProcessingSteps(false); // ❌ KALDIRILDI
      console.error('[PartialPayment] Belge iptal hatası:', error);
      showAlertModal('Hata', error.message || 'Belge iptal edilirken bir hata oluştu');
    }
  };

  // ✅ YENİ: Ödeme iade et
  // ✅ YENİ: Gerçek İade İşlemi (Belge kapandıktan sonra)
  const handleProcessRealRefund = async (payment: PaymentHistoryItem) => {
    try {
      console.log(`[PartialPayment] 💳 Gerçek iade yapılıyor: ${payment.label} - ${payment.amount} TL`);
      
      const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
      
      if (!fiscalDevice.refundPosPaymentAfterClose) {
        throw new Error('Gerçek POS iadesi desteklenmiyor');
      }
      
      // ✅ Belge kapandı, RefundEFTPayment kullanabiliriz!
      const refundResult = await fiscalDevice.refundPosPaymentAfterClose(
        payment.amount,
        payment.fiscalData?.AcquirerId,
        showAlertModal
      );
      
      if (refundResult.success) {
        console.log(`[PartialPayment] ✅ ${payment.label} başarıyla iade edildi`);
        return {
          success: true,
          message: `${payment.label} için ${payment.amount} TL başarıyla iade edildi! Kart sahibine iade slip'i verilecek.`
        };
      } else {
        console.error(`[PartialPayment] ❌ ${payment.label} iade edilemedi:`, refundResult.errorMessage);
        return {
          success: false,
          message: `${payment.label} için ${payment.amount} TL iade edilemedi.\n\nHata: ${refundResult.errorMessage}\n\nLÜTFEN POS TERMİNALİNDEN MANUEL İADE YAPIN:\nAuth Code: ${payment.fiscalData?.AuthCode || 'Bilinmiyor'}`
        };
      }
    } catch (error: any) {
      console.error(`[PartialPayment] ❌ ${payment.label} iade hatası:`, error);
      return {
        success: false,
        message: `${payment.label} için ${payment.amount} TL iade edilirken hata oluştu.\n\nLÜTFEN POS TERMİNALİNDEN MANUEL İADE YAPIN:\nAuth Code: ${payment.fiscalData?.AuthCode || 'Bilinmiyor'}`
      };
    }
  };

  // ✅ YENİ: Sıradaki ödemeyi iade et
  const handleRefundNextPayment = async () => {
    if (currentRefundIndex >= refundQueue.length) {
      // Tüm iadeler tamamlandı
      console.log('[PartialPayment] ✅ Tüm iadeler tamamlandı');
      
      // Başarı modalı göster
      const successCount = refundResults.filter(r => r.success).length;
      const failCount = refundResults.filter(r => !r.success).length;
      
      if (failCount === 0) {
        showAlertModal('✅ İadeler Tamamlandı', `${successCount} adet POS ödemesi başarıyla iade edildi!`);
      } else {
        showAlertModal('⚠️ İadeler Tamamlandı', `${successCount} başarılı, ${failCount} başarısız iade. Manuel iade gerekebilir.`);
      }
      
      // Modalı kapat - tüm işlemler zaten tamamlanmıştı
      setIsRealRefundModalOpen(false);
      setRefundQueue([]);
      setCurrentRefundIndex(0);
      setRefundResults([]);
      
      return;
    }
    
    // Şu anki ödemeyi iade et
    const currentPayment = refundQueue[currentRefundIndex];
    const result = await handleProcessRealRefund(currentPayment);
    
    // Sonucu kaydet
    setRefundResults(prev => [...prev, result]);
    
    // ✅ YENİ: Başarılı iade ise localStorage'dan sil
    if (result.success) {
      removePartialRefundFromStorage(currentPayment.id);
    }
    
    // Sıradaki ödemeye geç
    setCurrentRefundIndex(prev => prev + 1);
  };

  // ✅ YENİ: Gerçek iade modalını atla
  const handleSkipRealRefund = () => {
    console.log('[PartialPayment] ⏭️ Gerçek iade atlandı - kullanıcı sonra yapacak');
    
    // Sadece modalı kapat - tüm işlemler zaten tamamlandı
    setIsRealRefundModalOpen(false);
    setRefundQueue([]);
    setCurrentRefundIndex(0);
    setRefundResults([]);
  };

  // ✅ YENİ: Ödeme iade et (Parçalı ödeme modalında "↩️ İade" butonu)
  const handleRefundPayment = async (paymentItem: PaymentHistoryItem) => {
    try {
      console.log(`[PartialPayment] ↩️ Ödeme iade ediliyor: ${paymentItem.label} - ${paymentItem.amount} TL`);
      
      if (!paymentItem.paymentSequenceNo) {
        throw new Error('Ödeme sıra numarası bulunamadı');
      }

      setProcessingPaymentId(paymentItem.id);
      
      const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
      
      let result;
      if (paymentItem.type === 'cash') {
        if (!fiscalDevice.refundCashPayment) {
          throw new Error('Nakit iadesi desteklenmiyor');
        }
        result = await fiscalDevice.refundCashPayment(paymentItem.paymentSequenceNo, showAlertModal, paymentItem.amount);
      } else {
        if (!fiscalDevice.refundPosPayment) {
          throw new Error('POS iadesi desteklenmiyor');
        }
        // ✅ POS iadesi için fiscalData gerekli (AcquirerId, BatchNo, StanNo)
        result = await fiscalDevice.refundPosPayment(
          paymentItem.paymentSequenceNo, 
          showAlertModal,
          paymentItem.fiscalData,
          paymentItem.amount
        );
      }
      
      setProcessingPaymentId(null);

      if (!result.success) {
        showAlertModal('İade Hatası', result.errorMessage || 'Ödeme iade edilemedi');
        return;
      }

      // ✅ INPOS M530 İÇİN ÖZEL: CancelSale ile belge iptal edildi, modal'ı kapat!
      if (selectedFiscalDevice === 'inpos_m530' && result.isPartialRefund) {
        console.log('[PartialPayment] ⚠️ Inpos M530: CancelSale ile belge iptal edildi, modal kapatılıyor...');
        
        // State'leri temizle
        setIsDocumentPrepared(false);
        setIsLiveDocumentMode(false);
        setLiveDocumentStartTime(null);
        setPaymentSequenceCounter(1);
        setProcessingPaymentId(null);
        
        // Ödemeleri temizle
        setPaymentHistory([]);
        setPaidByCash(0);
        setPaidByPosMap({});
        setPaidByOpenAccount(0);
        
        // LocalStorage'ı temizle
        const key = getLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
          console.log('[PartialPayment] LocalStorage temizlendi');
        }
        
        // ✅ Başarı mesajını göster
        let successMessage = '';
        if (paymentItem.type === 'cash') {
          successMessage = 
            `⚠️ UYARI: Belge İptal Edildi (Inpos M530)\n\n` +
            `Inpos M530'da belge açıkken TEK ÖDEME iadesi YAPILMIYOR!\n\n` +
            `❌ TÜM BELGE İPTAL EDİLDİ (CancelSale)\n\n` +
            `Bu işlem sonrasında yeni bir satış başlatabilirsiniz.`;
        } else {
          successMessage = 
            `⚠️ UYARI: Belge İptal Edildi (Inpos M530)\n\n` +
            `Inpos M530'da belge açıkken TEK ÖDEME iadesi YAPILMIYOR!\n\n` +
            `❌ TÜM BELGE İPTAL EDİLDİ (CancelSale)\n\n` +
            `💳 POS İADESİ İÇİN:\n` +
            `Cihaz otomatik POS iade ekranını açtı.\n` +
            `Lütfen kartı tekrar okutup onaylayın.\n\n` +
            `Bu işlem sonrasında yeni bir satış başlatabilirsiniz.`;
        }
        
        // Modal'ı kapat (deneme yapıldıysa numara yenilemesi tetiklenir)
        closeWithoutSuccess();
        
        // Uyarı mesajını göster
        showAlertModal('⚠️ Belge İptal Edildi', successMessage);
        
        return;
      }

      // Başarılı - Ödemeyi 'refunded' olarak işaretle (Hugin için)
      const updatedHistory = paymentHistory.map(item => {
        if (item.id === paymentItem.id) {
          return {
            ...item,
            status: 'refunded' as const,
          };
        }
        return item;
      });

      setPaymentHistory(updatedHistory);
      saveToLocalStorage(updatedHistory);
      
      // ✅ YENİ: Kısmi iade edilen POS ödemesini global storage'a kaydet
      if (paymentItem.type === 'pos' && result.isPartialRefund) {
        savePartialRefundToStorage({
          ...paymentItem,
          status: 'refunded',
        });
      }
      
      // Toplamları güncelle
      if (paymentItem.type === 'cash') {
        setPaidByCash(prev => prev - paymentItem.amount);
      } else if (paymentItem.type === 'pos' && paymentItem.posId) {
        setPaidByPosMap(prev => ({
          ...prev,
          [paymentItem.posId!]: (prev[paymentItem.posId!] || 0) - paymentItem.amount,
        }));
      }

      console.log(`[PartialPayment] ✅ Ödeme başarıyla iade edildi: ${paymentItem.label}`);
      
      // ✅ Başarı mesajını oluştur
      let successMessage = `${paymentItem.label} için ${paymentItem.amount} TL başarıyla iade edildi.`;
      
      // ⚠️ Kısmi iade ise cihaz türüne göre uyarı ekle
      if (result.isPartialRefund) {
        // NAKİT iadesi
        if (paymentItem.type === 'cash' && selectedFiscalDevice === 'inpos_m530') {
          successMessage = 
            `⚠️ UYARI: Belge İptal Edildi (Inpos M530)\n\n` +
            `Inpos M530'da belge açıkken TEK ÖDEME iadesi YAPILMIYOR!\n\n` +
            `❌ TÜM BELGE İPTAL EDİLDİ (CancelSale)\n\n` +
            `Bu işlem sonrasında yeni bir satış başlatabilirsiniz.`;
        }
        // POS iadesi
        else if (paymentItem.type === 'pos') {
          if (selectedFiscalDevice === 'hugin_t300') {
            // Hugin T300 uyarısı
            successMessage = 
              `⚠️ UYARI: POS İadesi Kısıtlaması (Hugin T300)\n\n` +
              `${paymentItem.label} için ${paymentItem.amount} TL belgeden silindi.\n\n` +
              `❌ ANCAK: Karttan çekilen para iade EDİLMEDİ!\n\n` +
              `GERÇEK POS İADESİ için:\n` +
              `1. Belgeyi tamamlayın\n` +
              `2. POS terminalinden manuel iade yapın\n\n` +
              `Açık belge varken Hugin T300 gerçek POS iadesi yapamıyor.`;
          } else if (selectedFiscalDevice === 'inpos_m530') {
            // Inpos M530 uyarısı
            successMessage = 
              `⚠️ UYARI: Belge İptal Edildi (Inpos M530)\n\n` +
              `Inpos M530'da belge açıkken TEK ÖDEME iadesi YAPILMIYOR!\n\n` +
              `❌ TÜM BELGE İPTAL EDİLDİ (CancelSale)\n\n` +
              `💳 POS İADESİ İÇİN:\n` +
              `Cihaz otomatik POS iade ekranını açtı.\n` +
              `Lütfen kartı tekrar okutup onaylayın.\n\n` +
              `Bu işlem sonrasında yeni bir satış başlatabilirsiniz.`;
          }
        }
      }
      
      showAlertModal(
        result.isPartialRefund ? '⚠️ Kısmi İade' : '✅ İade Başarılı', 
        successMessage
      );
      
    } catch (error: any) {
      setProcessingPaymentId(null);
      console.error('[PartialPayment] Ödeme iade hatası:', error);
      showAlertModal('Hata', error.message || 'Ödeme iade edilirken bir hata oluştu');
    }
  };

  // ✅ YENİ: handleCompletePayment - MANUEL HISTORY ile (State güncellenmeden önce)
  const handleCompletePaymentWithHistory = async (manualHistory: PaymentHistoryItem[], skipFiscalCheck: boolean = false) => {
    try {
      console.log('[PartialPayment] Ödeme tamamlama başlıyor (manuel history)...');
      console.log('[PartialPayment] Manuel ödeme geçmişi:', manualHistory);
      console.log('[PartialPayment] Yazarkasa kontrolü atla:', skipFiscalCheck);

      // 1. Pending ödemeleri topla (MANUEL HISTORY'den)
      const pendingCashPayments = manualHistory.filter(h => h.type === 'cash' && h.status === 'pending');
      const pendingPosPayments = manualHistory.filter(h => h.type === 'pos' && h.status === 'pending');
      const pendingOpenAccountPayments = manualHistory.filter(h => h.type === 'open_account' && h.status === 'pending');

      const totalCash = pendingCashPayments.reduce((sum, p) => sum + p.amount, 0);
      const totalPos = pendingPosPayments.reduce((sum, p) => sum + p.amount, 0);
      const totalOpenAccount = pendingOpenAccountPayments.reduce((sum, p) => sum + p.amount, 0);

      console.log(`[PartialPayment] NAKİT: ${totalCash} TL`);
      console.log(`[PartialPayment] POS: ${totalPos} TL (${pendingPosPayments.length} adet)`);
      console.log(`[PartialPayment] AÇIK HESAP: ${totalOpenAccount} TL`);

      // ⚠️ Rest-P2P modu: Fiscal işlemi modal içinde YAPMA.
      //    Ödeme özetini topla, modalı kapat; usePayment.processPaymentAfterPartialModal
      //    Rest-P2P akışını (nakit fire-and-forget / pos-karma waiting modal) yürütecek.
      if (isRestP2PEnabled) {
        console.log('[PartialPayment] 🟣 Rest-P2P modu: Fiscal atlanıp ödeme usePayment Rest-P2P akışına devrediliyor');
        const posMap: { [key: string]: number } = {};
        // ✅ Rest-P2P için her ödeme AYRI part (aynı POS olsa bile).
        //   Aksi halde 3 ayrı kart okutması, posMap birleştirmesi nedeniyle
        //   tek 'pos' moduna düşüyor → Pavo cihazı tek seferde tüm tutarı
        //   tahsil ediyor. Bu liste her bir history kaydını korur.
        const pavoSplitPosParts: Array<{ posId: string; amount: number; label?: string }> = [];
        pendingPosPayments.forEach(p => {
          if (p.posId) {
            posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
            pavoSplitPosParts.push({ posId: p.posId, amount: p.amount });
          }
        });
        const totalPaid = totalCash + totalPos + totalOpenAccount;
        const paymentDetails: PaymentDetails = {
          paidByCash: totalCash,
          paidByPosMap: posMap,
          paidByOpenAccount: totalOpenAccount,
          totalPaid,
          remainingAmount: totalAmount - totalPaid,
          changeAmount: totalPaid > totalAmount ? totalPaid - totalAmount : 0,
          fiscalProcessed: false,
          fiscalData: null,
          pavoSplitPosParts,
        };
        console.log(`[PartialPayment] 🟣 Rest-P2P için ${pavoSplitPosParts.length} ayrı POS parçası hazırlandı (posMap=${Object.keys(posMap).length} key)`);
        const key = getLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
          console.log('[PartialPayment] LocalStorage temizlendi');
        }
        onClose(paymentDetails);
        return;
      }

      // Canli belge acik ise (recovery/parcali odeme) odemeler tek tek gonderilir
      if (isDocumentPrepared) {
        console.log('[PartialPayment] Belge zaten acik - canli belge uzerinden odeme yapiliyor...');
        setPaymentHistory(manualHistory);

        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        if (!fiscalDevice.addSinglePayment) {
          throw new Error('Tek odeme ekleme desteklenmiyor');
        }

        const allPending = [...pendingPosPayments, ...pendingCashPayments];
        let seqCounter = paymentSequenceCounter;
        let localHistory = [...manualHistory];
        let localPaidCash = paidByCash;
        const localPaidPosMap: { [key: string]: number } = { ...paidByPosMap };

        for (const payment of allPending) {
          if (payment.type === 'pos') setIsProcessingPosPayment(true);

          markFiscalAttempt();
          const payResult = await fiscalDevice.addSinglePayment(
            payment.type === 'cash' ? 'cash' : 'pos',
            payment.amount,
            showAlertModal
          );

          if (payment.type === 'pos') setIsProcessingPosPayment(false);

          if (!payResult.success) {
            showAlertModal('Ödeme Hatası', payResult.errorMessage || 'Ödeme eklenemedi');
            setPaymentHistory(localHistory);
            return;
          }

          localHistory = localHistory.map(h =>
            h.id === payment.id
              ? { ...h, status: 'paid' as const, paymentSequenceNo: seqCounter, fiscalData: payResult.fiscalData }
              : h
          );
          seqCounter++;

          if (payment.type === 'cash') {
            localPaidCash += payment.amount;
          } else if (payment.posId) {
            localPaidPosMap[payment.posId] = (localPaidPosMap[payment.posId] || 0) + payment.amount;
          }
        }

        setPaymentHistory(localHistory);
        setPaymentSequenceCounter(seqCounter);
        setPaidByCash(localPaidCash);
        setPaidByPosMap(localPaidPosMap);

        await handleCloseDocument({
          paidByCash: localPaidCash,
          paidByPosMap: localPaidPosMap,
          paidByOpenAccount,
        });
        return;
      }

      console.log('[PartialPayment] Belge hazırlanmamış - Hızlı Mod aktif (tek seferde ödeme)');
      
      // Fiscal device al
      const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
      
      // ✅ YENİ: Yazarkasa bağlantı kontrolü (NAKİT veya POS için)
      // skipFiscalDevice global flag'i true ise kontrol yapma (modal açılışında onaylanmış)
      // ⚠️ Rest-P2P modunda yerel servis bağlantı kontrolü atlanır (SignalR ile yönetilir)
      if ((totalCash > 0 || totalPos > 0) && !skipFiscalCheck && !skipFiscalDevice && !isRestP2PEnabled) {
        console.log('[PartialPayment] 🔌 Yazarkasa bağlantısı kontrol ediliyor...');
        let fiscalDeviceConnected = false;
        try {
          fiscalDeviceConnected = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
          console.log(`[PartialPayment] 🔌 Bağlantı durumu: ${fiscalDeviceConnected ? 'BAĞLI ✅' : 'BAĞLI DEĞİL ❌'}`);
        } catch (connError) {
          console.error('[PartialPayment] 🔌 Bağlantı kontrolü hatası:', connError);
          fiscalDeviceConnected = false;
        }

        // ✅ Yazarkasa BAĞLI DEĞİLSE → Onay modalı göster
        if (!fiscalDeviceConnected) {
          console.log('[PartialPayment] ⚠️ Yazarkasa bağlı değil! Onay modalı gösteriliyor...');
          
          // Callback'i sakla (kullanıcı onaylarsa çağrılacak)
          setPendingPaymentAction(() => {
            return () => handleCompletePaymentWithHistory(manualHistory, true); // skipFiscalCheck = true
          });
          
          // Onay modalını aç
          setIsFiscalDisconnectedConfirmOpen(true);
          return;
        }
      }
      
      // Ürünleri hazırla
      const fiscalProducts = cart.map(cartItem => {
        const inventoryItem = inventoryItems.find(inv => inv.id === cartItem.id);
        return {
          id: cartItem.id,
          name: cartItem.name,
          quantity: cartItem.quantity,
          price: cartItem.price,
          vatRate: cartItem.vatRate || 20,
          departmentNo: inventoryItem?.departmentNo || 1,
        };
      });
      
      // Beko cihazlar: Birden fazla ödeme girişi varsa tüm ödemeleri processMultiplePayments ile tek seferde gönder
      const isBekoPartialWithHistory = (selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') 
        && manualHistory.filter(h => h.status === 'pending').length > 1 
        && !skipFiscalDevice 
        && !skipFiscalCheck;
      
      if (isBekoPartialWithHistory && fiscalDevice.processMultiplePayments) {
        console.log('[PartialPayment] → Beko parçalı ödeme - tüm ödemeler toplanıp tek seferde gönderiliyor...');
        
        try {
          setIsProcessingPosPayment(true);
          
          const posPaymentsForFiscal = pendingPosPayments.map(p => ({
            id: p.id,
            amount: p.amount,
          }));

          markFiscalAttempt();
          const fiscalResult = await fiscalDevice.processMultiplePayments(
            fiscalProducts,
            posPaymentsForFiscal,
            totalCash > 0 ? totalCash : undefined,
            showAlertModal
          );
          
          setIsProcessingPosPayment(false);
          
          if (!fiscalResult.success) {
            showAlertModal('Yazarkasa Hatası', fiscalResult.errorMessage || 'Parçalı ödeme başarısız');
            return;
          }
          
          console.log('[PartialPayment] ✅ Beko parçalı ödeme başarılı');
          
          const posMap: { [key: string]: number } = {};
          pendingPosPayments.forEach(p => {
            if (p.posId) {
              posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
            }
          });
          
          const paymentDetails: PaymentDetails = {
            paidByCash: totalCash,
            paidByPosMap: posMap,
            paidByOpenAccount: totalOpenAccount,
            totalPaid: totalCash + totalPos + totalOpenAccount,
            remainingAmount: totalAmount - (totalCash + totalPos + totalOpenAccount),
            changeAmount: (totalCash + totalPos + totalOpenAccount) > totalAmount 
              ? (totalCash + totalPos + totalOpenAccount) - totalAmount 
              : 0,
            fiscalProcessed: true,
            fiscalData: fiscalResult.fiscalData,
          };
          
          const key = getLocalStorageKey();
          if (key) {
            localStorage.removeItem(key);
            console.log('[PartialPayment] LocalStorage temizlendi');
          }
          
          onClose(paymentDetails);
          return;
        } catch (error: any) {
          setIsProcessingPosPayment(false);
          throw error;
        }
      }

      // Sadece NAKİT varsa → processCashPayment gibi çalış
      if (totalCash > 0 && totalPos === 0) {
        let cashResult: { success: boolean; errorMessage?: string; fiscalData?: any } = { success: false };
        let fiscalProcessed = false;
        
        // ✅ Yazarkasa atlandıysa veya skipFiscalCheck true ise fiscal işlem yapma
        if (skipFiscalDevice || skipFiscalCheck) {
          console.log('[PartialPayment] → NAKİT ödeme (yazarkasasız mod) - Fiscal işlem atlanıyor...');
          cashResult = { success: true };
          fiscalProcessed = false;
        } else {
          console.log('[PartialPayment] → NAKİT ödeme (hızlı mod) - Yazarkasaya gönderiliyor...');
          markFiscalAttempt();
          cashResult = await fiscalDevice.processCashPayment(fiscalProducts, totalCash, showAlertModal, kitchenOrderCode, receiptCompanyName, useExternalPrinterForPavo);
          fiscalProcessed = true;
        }
        
        if (!cashResult.success) {
          showAlertModal('Yazarkasa Hatası', cashResult.errorMessage || 'Nakit ödeme başarısız');
          return;
        }
        
        console.log(`[PartialPayment] ✅ NAKİT ödeme başarılı (${fiscalProcessed ? 'hızlı mod' : 'yazarkasasız'})`);
        
        const paymentDetails: PaymentDetails = {
          paidByCash: totalCash,
          paidByPosMap: {},
          paidByOpenAccount: totalOpenAccount,
          totalPaid: totalCash + totalOpenAccount,
          remainingAmount: totalAmount - (totalCash + totalOpenAccount),
          changeAmount: (totalCash + totalOpenAccount) > totalAmount ? (totalCash + totalOpenAccount) - totalAmount : 0,
          fiscalProcessed: fiscalProcessed,
          fiscalData: cashResult.fiscalData,
        };
        
        // ✅ LocalStorage'ı temizle
        const key = getLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
          console.log('[PartialPayment] LocalStorage temizlendi');
        }
        
        onClose(paymentDetails);
        return;
      }
      
      // Sadece POS varsa → processPosPayment gibi çalış
      if (totalPos > 0 && totalCash === 0) {
        let posResult: { success: boolean; errorMessage?: string; fiscalData?: any } = { success: false };
        let fiscalProcessed = false;
        
        // ✅ Yazarkasa atlandıysa veya skipFiscalCheck true ise fiscal işlem yapma
        if (skipFiscalDevice || skipFiscalCheck) {
          console.log('[PartialPayment] → POS ödeme (yazarkasasız mod) - Fiscal işlem atlanıyor...');
          posResult = { success: true };
          fiscalProcessed = false;
        } else {
          console.log('[PartialPayment] → POS ödeme (hızlı mod) - Yazarkasaya gönderiliyor...');
          
          try {
            // ✅ Mavi "Ödeme Bekleniyor" modalını aç
            setIsProcessingPosPayment(true);
            
            markFiscalAttempt();
            posResult = await fiscalDevice.processPosPayment(fiscalProducts, totalPos, showAlertModal, undefined, undefined, kitchenOrderCode, receiptCompanyName, useExternalPrinterForPavo);
            
            // ✅ Modalı kapat
            setIsProcessingPosPayment(false);
            fiscalProcessed = true;
          } catch (error: any) {
            setIsProcessingPosPayment(false);
            throw error;
          }
        }
        
        if (!posResult.success) {
          showAlertModal('Yazarkasa Hatası', posResult.errorMessage || 'POS ödeme başarısız');
          return;
        }
        
        console.log(`[PartialPayment] ✅ POS ödeme başarılı (${fiscalProcessed ? 'hızlı mod' : 'yazarkasasız'})`);
        
        const posMap: { [key: string]: number } = {};
        pendingPosPayments.forEach(p => {
          // ✅ p.posId = POS seçeneğinin gerçek ID'si (banka hesabı bilgisi için gerekli)
          if (p.posId) {
            posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
          }
        });
        
        const paymentDetails: PaymentDetails = {
          paidByCash: 0,
          paidByPosMap: posMap,
          paidByOpenAccount: totalOpenAccount,
          totalPaid: totalPos + totalOpenAccount,
          remainingAmount: totalAmount - (totalPos + totalOpenAccount),
          changeAmount: (totalPos + totalOpenAccount) > totalAmount ? (totalPos + totalOpenAccount) - totalAmount : 0,
          fiscalProcessed: fiscalProcessed,
          fiscalData: posResult.fiscalData,
        };
        
        // ✅ LocalStorage'ı temizle
        const key = getLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
          console.log('[PartialPayment] LocalStorage temizlendi');
        }
        
        onClose(paymentDetails);
        return;
      }
      
      // ✅ YENİ: NAKİT + POS karışık ödeme (yazarkasasız mod)
      if (totalCash > 0 && totalPos > 0) {
        // Yazarkasasız modda karışık ödeme destekleniyor
        if (skipFiscalDevice || skipFiscalCheck) {
          console.log('[PartialPayment] → NAKİT + POS karışık ödeme (yazarkasasız mod)');
          
          const posMap: { [key: string]: number } = {};
          pendingPosPayments.forEach(p => {
            // ✅ p.posId = POS seçeneğinin gerçek ID'si (banka hesabı bilgisi için gerekli)
            if (p.posId) {
              posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
            }
          });
          
          const paymentDetails: PaymentDetails = {
            paidByCash: totalCash,
            paidByPosMap: posMap,
            paidByOpenAccount: totalOpenAccount,
            totalPaid: totalCash + totalPos + totalOpenAccount,
            remainingAmount: totalAmount - (totalCash + totalPos + totalOpenAccount),
            changeAmount: (totalCash + totalPos + totalOpenAccount) > totalAmount 
              ? (totalCash + totalPos + totalOpenAccount) - totalAmount 
              : 0,
            fiscalProcessed: false,
            fiscalData: null,
          };
          
          // ✅ LocalStorage'ı temizle
          const key = getLocalStorageKey();
          if (key) {
            localStorage.removeItem(key);
            console.log('[PartialPayment] LocalStorage temizlendi');
          }
          
          console.log('[PartialPayment] ✅ NAKİT + POS karışık ödeme başarılı (yazarkasasız)');
          onClose(paymentDetails);
          return;
        } else {
          const isBekoDeviceMixed = selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
          if (isBekoDeviceMixed && fiscalDevice.processMultiplePayments) {
            console.log('[PartialPayment] → Beko karışık ödeme - processMultiplePayments ile tek seferde gönderiliyor...');
            
            try {
              setIsProcessingPosPayment(true);
              
              const posPaymentsForFiscal = pendingPosPayments.map(p => ({
                id: p.id,
                amount: p.amount,
              }));

              markFiscalAttempt();
              const fiscalResult = await fiscalDevice.processMultiplePayments(
                fiscalProducts,
                posPaymentsForFiscal,
                totalCash > 0 ? totalCash : undefined,
                showAlertModal
              );
              
              setIsProcessingPosPayment(false);
              
              if (!fiscalResult.success) {
                showAlertModal('Yazarkasa Hatası', fiscalResult.errorMessage || 'Karışık ödeme başarısız');
                return;
              }
              
              console.log('[PartialPayment] ✅ Beko karışık ödeme başarılı');
              
              const posMap: { [key: string]: number } = {};
              pendingPosPayments.forEach(p => {
                if (p.posId) {
                  posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
                }
              });
              
              const paymentDetails: PaymentDetails = {
                paidByCash: totalCash,
                paidByPosMap: posMap,
                paidByOpenAccount: totalOpenAccount,
                totalPaid: totalCash + totalPos + totalOpenAccount,
                remainingAmount: totalAmount - (totalCash + totalPos + totalOpenAccount),
                changeAmount: (totalCash + totalPos + totalOpenAccount) > totalAmount 
                  ? (totalCash + totalPos + totalOpenAccount) - totalAmount 
                  : 0,
                fiscalProcessed: true,
                fiscalData: fiscalResult.fiscalData,
              };
              
              const key = getLocalStorageKey();
              if (key) {
                localStorage.removeItem(key);
                console.log('[PartialPayment] LocalStorage temizlendi');
              }
              
              onClose(paymentDetails);
              return;
            } catch (error: any) {
              setIsProcessingPosPayment(false);
              throw error;
            }
          } else {
            showAlertModal('Uyarı', 'Karışık ödeme (nakit + POS) için önce "Belgeyi Hazırla" butonuna basmalısınız!');
            return;
          }
        }
      }
      
    } catch (error: any) {
      setIsProcessingSteps(false);
      console.error('[PartialPayment] Ödeme tamamlama hatası:', error);
      showAlertModal('Ödeme Hatası', error.message || 'Ödeme tamamlanırken bir hata oluştu');
    }
  };

  const handleCompletePayment = async () => {
    try {
      console.log('[PartialPayment] Ödeme tamamlama başlıyor...');
      console.log('[PartialPayment] Ödeme geçmişi:', paymentHistory);

      // 1. Pending ödemeleri topla
      const pendingCashPayments = paymentHistory.filter(h => h.type === 'cash' && h.status === 'pending');
      const pendingPosPayments = paymentHistory.filter(h => h.type === 'pos' && h.status === 'pending');
      const pendingOpenAccountPayments = paymentHistory.filter(h => h.type === 'open_account' && h.status === 'pending');

      const totalCash = pendingCashPayments.reduce((sum, p) => sum + p.amount, 0);
      const totalPos = pendingPosPayments.reduce((sum, p) => sum + p.amount, 0);
      const totalOpenAccount = pendingOpenAccountPayments.reduce((sum, p) => sum + p.amount, 0);

      console.log(`[PartialPayment] NAKİT: ${totalCash} TL`);
      console.log(`[PartialPayment] POS: ${totalPos} TL (${pendingPosPayments.length} adet)`);
      console.log(`[PartialPayment] AÇIK HESAP: ${totalOpenAccount} TL`);

      // ⚠️ Rest-P2P modu: Fiscal işlemi modal içinde YAPMA; ödemeyi usePayment
      //    Rest-P2P akışına (nakit fire-and-forget / pos-karma waiting modal) devret.
      if (isRestP2PEnabled) {
        console.log('[PartialPayment] 🟣 Rest-P2P modu: Fiscal atlanıp ödeme usePayment Rest-P2P akışına devrediliyor');
        const posMap: { [key: string]: number } = {};
        // ✅ Rest-P2P için her ödeme AYRI part (aynı POS olsa bile).
        //   Aksi halde 3 ayrı kart okutması, posMap birleştirmesi nedeniyle
        //   tek 'pos' moduna düşüyor → Pavo cihazı tek seferde tüm tutarı
        //   tahsil ediyor. Bu liste her bir history kaydını korur.
        const pavoSplitPosParts: Array<{ posId: string; amount: number; label?: string }> = [];
        pendingPosPayments.forEach(p => {
          if (p.posId) {
            posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
            pavoSplitPosParts.push({ posId: p.posId, amount: p.amount });
          }
        });
        const totalPaid = totalCash + totalPos + totalOpenAccount;
        const paymentDetails: PaymentDetails = {
          paidByCash: totalCash,
          paidByPosMap: posMap,
          paidByOpenAccount: totalOpenAccount,
          totalPaid,
          remainingAmount: totalAmount - totalPaid,
          changeAmount: totalPaid > totalAmount ? totalPaid - totalAmount : 0,
          fiscalProcessed: false,
          fiscalData: null,
          pavoSplitPosParts,
        };
        console.log(`[PartialPayment] 🟣 Rest-P2P için ${pavoSplitPosParts.length} ayrı POS parçası hazırlandı (posMap=${Object.keys(posMap).length} key)`);
        const key = getLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
          console.log('[PartialPayment] LocalStorage temizlendi');
        }
        onClose(paymentDetails);
        return;
      }

      // ✅ YENİ: Belge hazırlanmamışsa ve tek ödeme varsa → Hızlı satış ekranındaki gibi çalıştır
      if (!isDocumentPrepared) {
        console.log('[PartialPayment] 🚀 Belge hazırlanmamış - Hızlı Mod aktif (tek seferde ödeme)');
        
        // Fiscal device al
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        // Ürünleri hazırla
        const fiscalProducts = cart.map(cartItem => {
          const inventoryItem = inventoryItems.find(inv => inv.id === cartItem.id);
          return {
            id: cartItem.id,
            name: cartItem.name,
            quantity: cartItem.quantity,
            price: cartItem.price,
            vatRate: cartItem.vatRate || 20,
            departmentNo: inventoryItem?.departmentNo || 1,
          };
        });
        
        // Beko cihazlar: Birden fazla ödeme girişi varsa tüm ödemeleri processMultiplePayments ile tek seferde gönder
        const pendingCount = paymentHistory.filter(h => h.status === 'pending').length;
        const isBekoPartialComplete = (selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') 
          && pendingCount > 1;
        
        if (isBekoPartialComplete && fiscalDevice.processMultiplePayments) {
          console.log(`[PartialPayment] → Beko parçalı ödeme (${pendingCount} adet) - tüm ödemeler tek seferde gönderiliyor...`);
          
          try {
            setIsProcessingPosPayment(true);
            
            const posPaymentsForFiscal = pendingPosPayments.map(p => ({
              id: p.id,
              amount: p.amount,
            }));

            markFiscalAttempt();
            const fiscalResult = await fiscalDevice.processMultiplePayments(
              fiscalProducts,
              posPaymentsForFiscal,
              totalCash > 0 ? totalCash : undefined,
              showAlertModal
            );
            
            setIsProcessingPosPayment(false);
            
            if (!fiscalResult.success) {
              showAlertModal('Yazarkasa Hatası', fiscalResult.errorMessage || 'Parçalı ödeme başarısız');
              return;
            }
            
            console.log('[PartialPayment] ✅ Beko parçalı ödeme başarılı');
            
            const posMap: { [key: string]: number } = {};
            pendingPosPayments.forEach(p => {
              if (p.posId) {
                posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
              }
            });
            
            const paymentDetails: PaymentDetails = {
              paidByCash: totalCash,
              paidByPosMap: posMap,
              paidByOpenAccount: totalOpenAccount,
              totalPaid: totalCash + totalPos + totalOpenAccount,
              remainingAmount: totalAmount - (totalCash + totalPos + totalOpenAccount),
              changeAmount: (totalCash + totalPos + totalOpenAccount) > totalAmount 
                ? (totalCash + totalPos + totalOpenAccount) - totalAmount 
                : 0,
              fiscalProcessed: true,
              fiscalData: fiscalResult.fiscalData,
            };
            
            onClose(paymentDetails);
            return;
          } catch (error: any) {
            setIsProcessingPosPayment(false);
            throw error;
          }
        }

        // Sadece NAKİT varsa → processCashPayment gibi çalış
        if (totalCash > 0 && totalPos === 0) {
          console.log('[PartialPayment] → NAKİT ödeme (hızlı mod) - Yazarkasaya gönderiliyor...');
          
          markFiscalAttempt();
          const cashResult = await fiscalDevice.processCashPayment(fiscalProducts, totalCash, showAlertModal, kitchenOrderCode, receiptCompanyName, useExternalPrinterForPavo);
          
          if (!cashResult.success) {
            showAlertModal('Yazarkasa Hatası', cashResult.errorMessage || 'Nakit ödeme başarısız');
            return;
          }
          
          console.log('[PartialPayment] ✅ NAKİT ödeme başarılı (hızlı mod)');
          
          const paymentDetails: PaymentDetails = {
            paidByCash: totalCash,
            paidByPosMap: {},
            paidByOpenAccount: totalOpenAccount,
            totalPaid: totalCash + totalOpenAccount,
            remainingAmount: totalAmount - (totalCash + totalOpenAccount),
            changeAmount: (totalCash + totalOpenAccount) > totalAmount ? (totalCash + totalOpenAccount) - totalAmount : 0,
            fiscalProcessed: true,
            fiscalData: cashResult.fiscalData,
          };
          
          onClose(paymentDetails);
          return;
        }
        
        // Sadece POS varsa → processPosPayment gibi çalış
        if (totalPos > 0 && totalCash === 0) {
          console.log('[PartialPayment] → POS ödeme (hızlı mod) - Yazarkasaya gönderiliyor...');
          
          try {
            // ✅ Mavi "Ödeme Bekleniyor" modalını aç
            setIsProcessingPosPayment(true);
            
            markFiscalAttempt();
            const posResult = await fiscalDevice.processPosPayment(fiscalProducts, totalPos, showAlertModal, undefined, undefined, kitchenOrderCode, receiptCompanyName, useExternalPrinterForPavo);
            
            // ✅ Modalı kapat
            setIsProcessingPosPayment(false);
            
            if (!posResult.success) {
              showAlertModal('Yazarkasa Hatası', posResult.errorMessage || 'POS ödeme başarısız');
              return;
            }
            
            console.log('[PartialPayment] ✅ POS ödeme başarılı (hızlı mod)');
            
            const posMap: { [key: string]: number } = {};
            pendingPosPayments.forEach(p => {
              if (p.posId) {
                posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
              }
            });
            
            const paymentDetails: PaymentDetails = {
              paidByCash: 0,
              paidByPosMap: posMap,
              paidByOpenAccount: totalOpenAccount,
              totalPaid: totalPos + totalOpenAccount,
              remainingAmount: totalAmount - (totalPos + totalOpenAccount),
              changeAmount: (totalPos + totalOpenAccount) > totalAmount ? (totalPos + totalOpenAccount) - totalAmount : 0,
              fiscalProcessed: true,
              fiscalData: posResult.fiscalData,
            };
            
            onClose(paymentDetails);
            return;
          } catch (error: any) {
            setIsProcessingPosPayment(false);
            throw error;
          }
        }
        
        // Karışık ödeme (NAKİT + POS)
        if (totalCash > 0 && totalPos > 0) {
          const isBekoDeviceMixed = selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
          if (isBekoDeviceMixed && fiscalDevice.processMultiplePayments) {
            console.log('[PartialPayment] → Beko karışık ödeme (hızlı mod) - processMultiplePayments ile gönderiliyor...');
            
            try {
              setIsProcessingPosPayment(true);
              
              const posPaymentsForFiscal = pendingPosPayments.map(p => ({
                id: p.id,
                amount: p.amount,
              }));

              markFiscalAttempt();
              const fiscalResult = await fiscalDevice.processMultiplePayments(
                fiscalProducts,
                posPaymentsForFiscal,
                totalCash > 0 ? totalCash : undefined,
                showAlertModal
              );
              
              setIsProcessingPosPayment(false);
              
              if (!fiscalResult.success) {
                showAlertModal('Yazarkasa Hatası', fiscalResult.errorMessage || 'Karışık ödeme başarısız');
                return;
              }
              
              console.log('[PartialPayment] ✅ Beko karışık ödeme başarılı (hızlı mod)');
              
              const posMap: { [key: string]: number } = {};
              pendingPosPayments.forEach(p => {
                if (p.posId) {
                  posMap[p.posId] = (posMap[p.posId] || 0) + p.amount;
                }
              });
              
              const paymentDetails: PaymentDetails = {
                paidByCash: totalCash,
                paidByPosMap: posMap,
                paidByOpenAccount: totalOpenAccount,
                totalPaid: totalCash + totalPos + totalOpenAccount,
                remainingAmount: totalAmount - (totalCash + totalPos + totalOpenAccount),
                changeAmount: (totalCash + totalPos + totalOpenAccount) > totalAmount 
                  ? (totalCash + totalPos + totalOpenAccount) - totalAmount 
                  : 0,
                fiscalProcessed: true,
                fiscalData: fiscalResult.fiscalData,
              };
              
              onClose(paymentDetails);
              return;
            } catch (error: any) {
              setIsProcessingPosPayment(false);
              throw error;
            }
          } else {
            showAlertModal('Uyarı', 'Karışık ödeme (nakit + POS) için önce "Belgeyi Hazırla" butonuna basmalısınız!');
            return;
          }
        }
      }

      // 2. Eğer POS veya NAKİT varsa, yazarkasaya gönder (BELGE HAZIRLANMIŞ MOD)
      if (totalPos > 0 || totalCash > 0) {

        // Canli belge acik ise (recovery dahil) odemeleri tek tek gonder ve kapat
        if (isDocumentPrepared) {
          console.log('[PartialPayment] Canli belge acik - odemeler tek tek ekleniyor...');
          const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
          if (!fiscalDevice.addSinglePayment) {
            throw new Error('Tek odeme ekleme desteklenmiyor');
          }

          const allPending = [...pendingPosPayments, ...pendingCashPayments];
          let seqCounter = paymentSequenceCounter;
          let localHistory = [...paymentHistory];
          let localPaidCash = paidByCash;
          const localPaidPosMap: { [key: string]: number } = { ...paidByPosMap };

          for (const payment of allPending) {
            if (payment.type === 'pos') setIsProcessingPosPayment(true);

            markFiscalAttempt();
            const payResult = await fiscalDevice.addSinglePayment(
              payment.type === 'cash' ? 'cash' : 'pos',
              payment.amount,
              showAlertModal
            );

            if (payment.type === 'pos') setIsProcessingPosPayment(false);

            if (!payResult.success) {
              showAlertModal('Ödeme Hatası', payResult.errorMessage || 'Ödeme eklenemedi');
              setPaymentHistory(localHistory);
              return;
            }

            localHistory = localHistory.map(h =>
              h.id === payment.id
                ? { ...h, status: 'paid' as const, paymentSequenceNo: seqCounter, fiscalData: payResult.fiscalData }
                : h
            );
            seqCounter++;

            if (payment.type === 'cash') {
              localPaidCash += payment.amount;
            } else if (payment.posId) {
              localPaidPosMap[payment.posId] = (localPaidPosMap[payment.posId] || 0) + payment.amount;
            }
          }

          setPaymentHistory(localHistory);
          setPaymentSequenceCounter(seqCounter);
          setPaidByCash(localPaidCash);
          setPaidByPosMap(localPaidPosMap);

          await handleCloseDocument({
            paidByCash: localPaidCash,
            paidByPosMap: localPaidPosMap,
            paidByOpenAccount,
          });
          return;
        }

        // ✅ YENİ: Ödeme adımlarını hazırla
        const steps: PaymentStep[] = [];
        
        // POS adımları
        pendingPosPayments.forEach((posPayment, index) => {
          steps.push({
            stepIndex: index,
            type: 'pos',
            label: posPayment.label,
            amount: posPayment.amount,
            status: 'waiting',
          });
        });
        
        // NAKİT adımı (varsa)
        if (totalCash > 0) {
          steps.push({
            stepIndex: pendingPosPayments.length,
            type: 'cash',
            label: 'Nakit Ödeme',
            amount: totalCash,
            status: 'waiting',
          });
        }
        
        setPaymentSteps(steps);
        setIsProcessingSteps(true);
        setCurrentFailedStep(null);
        
        // Fiscal device al
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        if (!fiscalDevice.processMultiplePayments) {
          throw new Error('Çoklu ödeme işlemi desteklenmiyor');
        }

        // Ürünleri hazırla (KDV HARİÇ - yazarkasa otomatik KDV ekler)
        const fiscalProducts = cart.map(cartItem => {
          const inventoryItem = inventoryItems.find(inv => inv.id === cartItem.id);

          return {
            id: cartItem.id,
            name: cartItem.name,
            quantity: cartItem.quantity,
            price: cartItem.price,
            vatRate: cartItem.vatRate || 20,
            departmentNo: inventoryItem?.departmentNo || 1,
          };
        });

        // POS ödemelerini hazırla
        const posPaymentsForFiscal = pendingPosPayments.map(p => ({
          id: p.id,
          amount: p.amount,
        }));

        console.log('[PartialPayment] Yazarkasaya gönderiliyor...');
        console.log('  Ürünler:', fiscalProducts.length);
        console.log('  POS Ödemeleri:', posPaymentsForFiscal.length);
        console.log('  NAKİT:', totalCash);

        // ✅ YENİ: Step callback fonksiyonları
        const stepCallback = {
          onStepStart: (stepIndex: number, stepType: 'pos' | 'cash', amount: number, label?: string) => {
            console.log(`[PartialPayment] ADIM BAŞLADI: ${stepIndex} - ${label} (${amount} TL)`);
            setPaymentSteps(prev => prev.map(step => 
              step.stepIndex === stepIndex 
                ? { ...step, status: 'processing' as const }
                : step
            ));
          },
          onStepSuccess: (stepIndex: number, stepType: 'pos' | 'cash', amount: number, data?: any) => {
            console.log(`[PartialPayment] ADIM BAŞARILI: ${stepIndex} (${amount} TL)`);
            setPaymentSteps(prev => prev.map(step => 
              step.stepIndex === stepIndex 
                ? { ...step, status: 'success' as const, fiscalData: data }
                : step
            ));
          },
          onStepError: (stepIndex: number, stepType: 'pos' | 'cash', amount: number, errorMessage: string) => {
            console.error(`[PartialPayment] ADIM HATALI: ${stepIndex} (${amount} TL) - ${errorMessage}`);
            setPaymentSteps(prev => prev.map(step => 
              step.stepIndex === stepIndex 
                ? { ...step, status: 'error' as const, errorMessage }
                : step
            ));
            setCurrentFailedStep(stepIndex);
          },
        };

        markFiscalAttempt();
        const fiscalResult = await fiscalDevice.processMultiplePayments(
          fiscalProducts,
          posPaymentsForFiscal,
          totalCash > 0 ? totalCash : undefined,
          showAlertModal,
          stepCallback
        );

        setIsProcessingSteps(false);

        if (!fiscalResult.success) {
          // ✅ HATA DURUMU: Belge ID'yi sakla (iptal için)
          if (fiscalResult.documentId) {
            setDocumentIdForCancel(fiscalResult.documentId);
          }
          
          // ✅ HATA MODALINI GÖSTERME - Step UI'da zaten gösteriliyor
          console.error('[PartialPayment] Yazarkasa işlemi başarısız:', fiscalResult.errorMessage);
          
          // Modal açık kalsın, kullanıcı Yeniden Dene veya İptal Et seçecek
          return;
        }

        console.log('[PartialPayment] ✅ Yazarkasa işlemi başarılı');

        // 3. Tüm pending ödemeleri 'paid' olarak işaretle ve fiscal data'yı sakla
        const updatedHistory = paymentHistory.map(item => {
          if (item.status === 'pending') {
            return {
              ...item,
              status: 'paid' as const,
              fiscalData: fiscalResult.fiscalData, // Yazarkasa verisini kaydet
            };
          }
          return item;
        });

        setPaymentHistory(updatedHistory);

        // 4. LocalStorage'a kaydet
        saveToLocalStorage(updatedHistory);
        
        // ✅ fiscal data'yı daha sonra kullanmak için state'e sakla
        setLastFiscalData(fiscalResult.fiscalData);
      }

      // 5. Toplamları hesapla ve modal'ı kapat
      let finalCashTotal = paidByCash + totalCash;
      let finalOpenAccountTotal = paidByOpenAccount + totalOpenAccount;
      let finalPosMap = { ...paidByPosMap };

      pendingPosPayments.forEach(item => {
        if (item.posId) {
          finalPosMap[item.posId] = (finalPosMap[item.posId] || 0) + item.amount;
        }
      });

      const finalTotalPaid = finalCashTotal + Object.values(finalPosMap).reduce((sum, amount) => sum + amount, 0) + finalOpenAccountTotal;

      const paymentDetails: PaymentDetails = {
        paidByCash: finalCashTotal,
        paidByPosMap: finalPosMap,
        paidByOpenAccount: finalOpenAccountTotal,
        totalPaid: finalTotalPaid,
        remainingAmount: totalAmount - finalTotalPaid,
        changeAmount: finalTotalPaid > totalAmount ? finalTotalPaid - totalAmount : 0,
        fiscalProcessed: totalPos > 0 || totalCash > 0, // ✅ Yazarkasa işlemi yapıldıysa true
        fiscalData: lastFiscalData, // ✅ Yazarkasa verisini ekle
      };

      console.log('[PartialPayment] Ödeme tamamlandı:', paymentDetails);

      // ✅ LocalStorage'ı temizle
      const key = getLocalStorageKey();
      if (key) {
        localStorage.removeItem(key);
        console.log('[PartialPayment] LocalStorage temizlendi');
      }
      
      // ✅ Fiscal data'yı temizle
      setLastFiscalData(null);
      
      // ✅ Adım state'lerini temizle
      setPaymentSteps([]);
      setIsProcessingSteps(false);
      setCurrentFailedStep(null);
      setDocumentIdForCancel(null);

      onClose(paymentDetails);
    } catch (error: any) {
      setIsProcessingSteps(false);
      console.error('[PartialPayment] Ödeme tamamlama hatası:', error);
      showAlertModal('Ödeme Hatası', error.message || 'Ödeme tamamlanırken bir hata oluştu');
    }
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // ✅ Belge açıksa modal kapatılmasın
          if (isDocumentPrepared) {
            showAlertModal('⚠️ Uyarı', 'Belge açık! Önce belgeyi kapatın veya iptal edin.');
            return;
          }
          closeWithoutSuccess();
        }}
        maxWidth="md"
        fullWidth 
        PaperProps={{sx: { 
          minHeight: isSmallScreen ? '80vh' : '75vh',
          maxHeight: '95vh',
          m: isSmallScreen ? 1 : 2
        }}}
      >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: isSmallScreen ? 1.5 : 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <span>Parçalı Ödeme</span>
          {isRestP2PEnabled && (
            <PavoLiveBadge
              deviceCode={restP2PDeviceCode}
              deviceName={restP2PDeviceName}
              size="small"
              onOnlineChange={setRestP2POnline}
            />
          )}
        </Box>
        <GuideButton guideKey="sales.partialPayment" tabIndex={-1} />
        <IconButton
          onClick={() => {
            if (isDocumentPrepared) {
              showAlertModal('⚠️ Uyarı', 'Belge açık! Önce belgeyi kapatın veya iptal edin.');
              return;
            }
            closeWithoutSuccess();
          }} 
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        {/* ÜST KISIM: TOPLAM BİLGİLER */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1.5} justifyContent="center">
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" display="block" align="center" color="textSecondary">
                TOPLAM TUTAR
              </Typography>
              <Typography variant={isSmallScreen ? 'h6' : 'h5'} align="center" sx={{fontWeight: 'bold'}}>
                {formatCurrency(totalAmount)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" display="block" align="center" color="textSecondary">
                ÖDENEN TUTAR
              </Typography>
              <Typography variant={isSmallScreen ? 'h6' : 'h5'} align="center" color="success.main" sx={{fontWeight: 'bold'}}>
                {formatCurrency(totalPaid)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" display="block" align="center" color="textSecondary">
                KALAN TUTAR
              </Typography>
              <Typography variant={isSmallScreen ? 'h6' : 'h5'} align="center" color={remainingAmount > 0 ? "error.main" : (remainingAmount < 0 ? "warning.main" : "text.primary")} sx={{fontWeight: 'bold'}}>
                {formatCurrency(remainingAmount)}
                 {remainingAmount < 0 && <Typography variant="caption" color="warning.dark" display="block">(Para Üstü)</Typography>}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" display="block" align="center" color="textSecondary">
                {isDocumentPrepared ? '🟢 BELGE DURUM' : 'BELGE DURUMU'}
              </Typography>
              <Typography variant="body2" align="center" sx={{ fontWeight: 'bold', color: isDocumentPrepared ? 'success.main' : 'text.secondary' }}>
                {isDocumentPrepared ? 'AÇIK' : 'Hazır Değil'}
              </Typography>
            </Grid>
          </Grid>
          
          {/* ✅ Timeout Uyarısı KALDIRILDI - Belge Durumu bölümünde yeterli bilgi var */}
        </Paper>

        {/* ANA İÇERİK: SOL (GEÇMIŞ) + SAĞ (NUMPAD) */}
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {/* SOL TARAF: ÖDEME YÖNTEMLERİ */}
          <Grid item xs={12} md={5}>
            <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Ödeme Yöntemleri
              </Typography>
              <Divider sx={{ mb: 1 }} />
              
              {paymentHistory.length === 0 ? (
                <Box sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body2">Henüz ödeme eklenmedi</Typography>
                </Box>
              ) : (
                <List dense sx={{ flexGrow: 1, overflow: 'auto' }}>
                  {paymentHistory.map((item, index) => (
                    <ListItem
                      key={item.id}
                      secondaryAction={
                        item.status === 'pending' ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* ✅ YENİ: Canlı Belge Modunda "Ödeme Ekle" butonu */}
                            {isDocumentPrepared && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleAddPayment(item)}
                                disabled={processingPaymentId === item.id}
                                sx={{ minWidth: 100 }}
                              >
                                {processingPaymentId === item.id ? (
                                  <>
                                    <CircularProgress size={16} sx={{ mr: 0.5 }} color="inherit" />
                                    İşleniyor
                                  </>
                                ) : (
                                  <>
                                    💳 Ödeme Ekle
                                  </>
                                )}
                              </Button>
                            )}
                            {/* Sil butonu - sadece işleniyor değilse aktif */}
                            <IconButton 
                              edge="end" 
                              size="small" 
                              onClick={() => handleDeleteClick(item.id)}
                              color="error"
                              disabled={processingPaymentId === item.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : item.status === 'paid' ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label="✓ Ödendi" color="success" size="small" />
                            {/* ✅ İade butonu - Hugin ve Ingenico için göster */}
                            {(selectedFiscalDevice === 'hugin_t300' || selectedFiscalDevice === 'ingenico_5000f') && (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={() => handleRefundPayment(item)}
                                disabled={processingPaymentId === item.id}
                                sx={{ minWidth: 80 }}
                              >
                                {processingPaymentId === item.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  '↩️ İade'
                                )}
                              </Button>
                            )}
                          </Box>
                        ) : item.status === 'refunded' ? (
                          <Chip label="↩️ İade Edildi" color="default" size="small" />
                        ) : null
                      }
                      sx={{
                        border: '1px solid',
                        borderColor: item.status === 'paid' ? 'success.main' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: item.status === 'paid' ? 'success.light' : 'background.paper',
                        opacity: item.status === 'paid' ? 1 : 0.85,
                        pr: isDocumentPrepared ? '170px' : '50px', // ✅ Sağ tarafta butonlar için alan bırak
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={item.label} 
                              size="small"
                              color={item.type === 'cash' ? 'success' : item.type === 'pos' ? 'primary' : 'warning'}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(item.amount)}
                            </Typography>
                            {item.status === 'pending' && !isDocumentPrepared && (
                              <Chip label="Bekliyor" size="small" color="default" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={`#${index + 1} - ${new Date(item.timestamp).toLocaleTimeString('tr-TR')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* SAĞ TARAF: NUMPAD VE BUTONLAR */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* ✅ Girdi Alanı - Üstte SABİT (Her zaman görünür) */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography 
                  variant="caption" 
                  display="block" 
                  align="center" 
                  sx={{ 
                    mb: 0.5, 
                    fontWeight: 'bold',
                    color: 'text.secondary'
                  }}
                >
                  {activePaymentType ? 
                    `${activePaymentType === 'cash' ? cashOptionLabel : 
                      activePaymentType === 'open_account' ? 'Açık Hesap' : 
                      posOptionsList.find(p => p.id === activePaymentType)?.label || 'POS'} - Tutar Giriniz` 
                    : 'Ödeme Türü Seçiniz'}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50px'}}>
                  <Typography 
                    variant="h4" 
                    sx={{
                      color: 'text.primary', 
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      fontFamily: 'monospace'
                    }}
                  >
                    {currentInput || '0.00'}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{
                      ml: 1, 
                      color: 'text.primary', 
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    TL
                  </Typography>
                </Box>
              </Paper>

              {/* Ödeme Butonları - BÜYÜTÜLDÜ */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {/* NAKİT */}
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={activePaymentType === 'cash' ? "contained" : "outlined"}
                    color="success"
                    onClick={() => handleSelectPaymentType('cash')}
                    sx={{ height: '65px', fontSize: '1rem', fontWeight: 'bold' }}
                  >
                    {cashOptionLabel}
                  </Button>
                </Grid>

                {/* POS BUTONLARI */}
                {posOptionsList.map((posOption) => (
                  <Grid item xs={6} key={posOption.id}>
                    <Button
                      fullWidth
                      variant={activePaymentType === posOption.id ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => handleSelectPaymentType(posOption.id)}
                      sx={{ height: '65px', fontSize: '1rem', fontWeight: 'bold' }}
                    >
                      {posOption.label}
                    </Button>
                  </Grid>
                ))}

                {/* AÇIK HESAP */}
                {openAccountOption && openAccountOption.enabled && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant={activePaymentType === 'open_account' ? "contained" : "outlined"}
                      color="warning"
                      onClick={() => handleSelectPaymentType('open_account')}
                      sx={{ height: '65px', fontSize: '1rem', fontWeight: 'bold' }}
                      disabled={isGeneralCustomer}
                    >
                      {openAccountOption.label}
                    </Button>
                  </Grid>
                )}
              </Grid>

              {/* Numpad */}
              <Paper elevation={0} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <Grid container spacing={isSmallScreen ? 0.5 : 1}>
                  {['1', '2', '3', 'C'].map((val) => (
                    <NumpadButton key={val} value={val} onClick={handleNumpadInput} color={val === 'C' ? 'error' : 'primary'} variant={val === 'C' ? 'contained' : 'outlined'} isSmallScreen={isSmallScreen} />
                  ))}
                  {['4', '5', '6', 'backspace'].map((val) => (
                    <NumpadButton key={val} value={val} onClick={handleNumpadInput} color={val === 'backspace' ? 'warning' : 'primary'} isSmallScreen={isSmallScreen} />
                  ))}
                  {['7', '8', '9', 'Enter'].map((val) => (
                    <NumpadButton key={val} value={val} onClick={handleNumpadInput} variant={val === 'Enter' ? 'contained' : 'outlined'} color={val === 'Enter' ? 'success' : 'primary'} isSmallScreen={isSmallScreen} />
                  ))}
                  <NumpadButton value="0" onClick={handleNumpadInput} xs={6} isSmallScreen={isSmallScreen} />
                  <NumpadButton value="." onClick={handleNumpadInput} xs={3} isSmallScreen={isSmallScreen} />
                  <NumpadButton value="00" onClick={handleNumpadInput} xs={3} disabled isSmallScreen={isSmallScreen} />
                </Grid>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: isSmallScreen ? 1.5 : 2, borderTop: '1px solid #eee', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={() => {
              if (isDocumentPrepared) {
                showAlertModal('⚠️ Uyarı', 'Belge açık! Önce belgeyi kapatın veya iptal edin.');
                return;
              }
              closeWithoutSuccess();
            }} 
            color="inherit" 
            size={isSmallScreen ? 'small' : 'medium'}
          >
            İptal
          </Button>
          
          {/* ✅ YENİ: Belge Açıksa "Belgeyi İptal Et" butonu */}
          {isDocumentPrepared && (
            <Button
              variant="contained"
              color="error"
              size={isSmallScreen ? 'small' : 'medium'}
              onClick={() => setIsCancelDocumentConfirmOpen(true)}
              disabled={isProcessingSteps}
              sx={{ fontWeight: 'bold' }}
            >
              ❌ Belgeyi İptal Et
            </Button>
          )}
        </Box>
        
        {/* ✅ YENİ: Canlı Belge Modunda farklı butonlar */}
        {/* ✅ PavoPay için: Son ödeme yapıldığında belge otomatik kapanır, bu butona gerek yok */}
        {isDocumentPrepared && selectedFiscalDevice !== 'pavopay' ? (
          <Button 
            onClick={() => handleCloseDocument()} 
            variant="contained" 
            color="success"
            size={isSmallScreen ? 'medium' : 'large'}
            disabled={
              isProcessingSteps || 
              Math.abs(remainingAmount) > 0.001 || 
              paymentHistory.some(p => p.status === 'pending')
            }
          >
            {isProcessingSteps ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Kapatılıyor...
              </>
            ) : paymentHistory.some(p => p.status === 'pending') ? (
              '⚠️ Önce Tüm Ödemelerden "Ödeme Al"'
            ) : (
              '🔒 Belgeyi Kapat & Tamamla'
            )}
          </Button>
        ) : isDocumentPrepared && selectedFiscalDevice === 'pavopay' ? (
          // ✅ PavoPay için: Kalan tutar 0 olduğunda otomatik kapanacak, bu buton bilgilendirme amaçlı
          <Button 
            variant="contained" 
            color="info"
            size={isSmallScreen ? 'medium' : 'large'}
            disabled={true}
          >
            {Math.abs(remainingAmount) > 0.001 
              ? '💳 Son ödemeyi alın, belge otomatik kapanacak'
              : '✅ Ödeme tamamlandı, işlem kapanıyor...'
            }
          </Button>
        ) : (
          <Button 
            onClick={handleCompletePayment} 
            variant="contained" 
            color={isRestP2PEnabled && !restP2POnline ? 'warning' : 'primary'}
            size={isSmallScreen ? 'medium' : 'large'}
            // ℹ️ Rest-P2P offline iken de tıklanabilir: akış başlar,
            // PavoRestP2PWaitModal "Yine de Devam Et" sunar (mali fişsiz SQL kaydı).
            disabled={
              (remainingAmount > 0 && Math.abs(remainingAmount) > 0.001) || 
              isProcessingSteps || 
              currentFailedStep !== null
            }
          >
            {isRestP2PEnabled && !restP2POnline
              ? '⚠️ Pavo Çevrimdışı — Yine de Dene'
              : remainingAmount < -0.001 
                ? `Ödemeyi Tamamla (${formatCurrency(changeAmount)} Para Üstü)` 
                : 'Ödemeyi Tamamla (Hızlı)'
            }
          </Button>
        )}
      </DialogActions>

      {/* ✅ YENİ: Silme Onay Modalı */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ödemeyi Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu ödemeyi silmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ (Ingenico): Ödeme Hatası — Yine de Devam Et Onay Modalı */}
      <Dialog
        open={failedPaymentForContinue !== null}
        onClose={() => { setFailedPaymentForContinue(null); setFailedPaymentErrorMsg(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          ⚠️ Ödeme Hatası — Cihaz Durumu Doğrulanamadı
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {failedPaymentErrorMsg || 'Ödeme eklenirken bir hata oluştu.'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{failedPaymentForContinue?.label}</strong> — <strong>{failedPaymentForContinue?.amount.toFixed(2)} TL</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ödemenin cihazdan alınıp alınmadığı doğrulanamadı. Lütfen yazarkasa ekranını
            ve POS slibini kontrol edin.
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
            Ödeme cihazdan ALINDIYSA "Yine de Devam Et" ile ödemeyi alınmış sayabilirsiniz.
            Alınmadıysa "İptal" deyip tekrar deneyin.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexDirection: 'column' }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="large"
            onClick={handleContinueFailedPayment}
            sx={{ py: 1.5, fontWeight: 'bold' }}
          >
            ✅ Yine de Devam Et (Ödeme Alındı)
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            size="large"
            onClick={() => { setFailedPaymentForContinue(null); setFailedPaymentErrorMsg(''); }}
            sx={{ py: 1.5, fontWeight: 'bold' }}
          >
            ❌ İptal (Ödeme Alınmadı)
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Belge İptal Onay Modalı */}
      <Dialog
        open={isCancelDocumentConfirmOpen}
        onClose={() => setIsCancelDocumentConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          ⚠️ Belgeyi İptal Et
        </DialogTitle>
        <DialogContent>
          {paymentHistory.some(p => p.status === 'paid') ? (
            <>
              <Typography variant="body1" sx={{ mb: 2, color: 'error.main', fontWeight: 'bold' }}>
                ⚠️ UYARI: Alınmış ödemeler var!
              </Typography>
              {selectedFiscalDevice === 'ingenico_5000f' ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Ödemeleri tek tek iade edebilir veya cihaz resetlenmişse tümünü sıfırlayabilirsiniz.
                  </Typography>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                    Cihaz resetlendiyse veya bağlantı koptuysa "Zorla İptal Et" butonunu kullanın.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Bu belge iptal edilemez çünkü yazarkasaya gönderilmiş ve alınmış ödemeler var.
                  </Typography>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                    📋 Lütfen önce alınan ödemeleri iade ediniz!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ödeme listesindeki "✓ Ödendi" durumundaki ödemelerin yanındaki "↩️ İade" butonuna basarak ödemeleri iade edebilirsiniz.
                  </Typography>
                </>
              )}
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Yazarkasadaki açık belgeyi iptal etmek istediğinize emin misiniz?
              </Typography>
              <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                ❌ Bu işlem geri alınamaz! Tüm eklenen ödemeler silinecek.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCancelDocumentConfirmOpen(false)} variant="outlined">
            {paymentHistory.some(p => p.status === 'paid') && selectedFiscalDevice !== 'ingenico_5000f' ? 'Tamam' : 'Vazgeç'}
          </Button>
          {paymentHistory.some(p => p.status === 'paid') && selectedFiscalDevice === 'ingenico_5000f' && (
            <Button 
              onClick={() => {
                setIsCancelDocumentConfirmOpen(false);
                handleCancelLiveDocument();
              }} 
              variant="contained" 
              color="error"
              sx={{ fontWeight: 'bold' }}
            >
              ❌ Zorla İptal Et
            </Button>
          )}
          {!paymentHistory.some(p => p.status === 'paid') && (
            <Button 
              onClick={() => {
                setIsCancelDocumentConfirmOpen(false);
                handleCancelLiveDocument();
              }} 
              variant="contained" 
              color="error"
              sx={{ fontWeight: 'bold' }}
            >
              ❌ Evet, İptal Et
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Gerçek İade Modalı (Belge kapandıktan sonra) */}
      <Dialog
        open={isRealRefundModalOpen}
        onClose={() => {
          // ✅ Modal kapatılabilir - kullanıcı sonra yapabilir
          setIsRealRefundModalOpen(false);
          setRefundQueue([]);
          setCurrentRefundIndex(0);
          setRefundResults([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          💳 Gerçek POS İadesi Gerekli
          <IconButton 
            onClick={() => {
              setIsRealRefundModalOpen(false);
              setRefundQueue([]);
              setCurrentRefundIndex(0);
              setRefundResults([]);
            }} 
            size="small"
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Belge iptal edildi ancak <strong>{refundQueue.length} adet POS ödemesi</strong> için <strong>gerçek iade</strong> yapılması gerekiyor.
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          {/* İade Kuyruğu Listesi */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            İade Edilecek Ödemeler:
          </Typography>
          <List>
            {refundQueue.map((payment, index) => {
              const isCompleted = index < currentRefundIndex;
              const isCurrent = index === currentRefundIndex;
              const result = refundResults[index];
              
              return (
                <ListItem
                  key={payment.id}
                  sx={{
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: isCompleted 
                      ? (result?.success ? '#e8f5e9' : '#ffebee')
                      : isCurrent
                      ? '#fff3e0'
                      : '#fff'
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isCompleted ? (
                          result?.success ? (
                            <Chip label="✅ İade Edildi" color="success" size="small" />
                          ) : (
                            <Chip label="❌ Başarısız" color="error" size="small" />
                          )
                        ) : isCurrent ? (
                          <Chip label="🔄 İşleniyor..." color="warning" size="small" />
                        ) : (
                          <Chip label="⏳ Bekliyor" color="default" size="small" />
                        )}
                        <Typography variant="body1" fontWeight="bold">
                          {payment.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {payment.amount} TL
                        </Typography>
                      </Box>
                    }
                    secondary={
                      result && !result.success ? (
                        <Typography variant="caption" color="error">
                          {result.message}
                        </Typography>
                      ) : isCompleted && result?.success ? (
                        <Typography variant="caption" color="success.main">
                          {result.message}
                        </Typography>
                      ) : isCurrent ? (
                        <Typography variant="caption" color="warning.main">
                          Auth Code: {payment.fiscalData?.AuthCode || 'Bilinmiyor'}
                        </Typography>
                      ) : null
                    }
                  />
                </ListItem>
              );
            })}
          </List>
          
          {/* İlerleme Göstergesi */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              İlerleme: {currentRefundIndex} / {refundQueue.length}
            </Typography>
            <Box sx={{ flexGrow: 1, height: 8, bgcolor: '#eee', borderRadius: 1, overflow: 'hidden' }}>
              <Box 
                sx={{ 
                  width: `${(currentRefundIndex / refundQueue.length) * 100}%`, 
                  height: '100%', 
                  bgcolor: 'primary.main',
                  transition: 'width 0.3s ease'
                }} 
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleSkipRealRefund}
            color="inherit"
          >
            ⏭️ Sonra Yap (Manuel)
          </Button>
          
          <Button 
            onClick={handleRefundNextPayment}
            variant="contained"
            color="primary"
            disabled={currentRefundIndex >= refundQueue.length}
          >
            {currentRefundIndex >= refundQueue.length 
              ? '✅ Tüm İadeler Tamamlandı'
              : currentRefundIndex === 0
              ? '💳 İade İşlemini Başlat'
              : '➡️ Sıradaki İadeye Geç'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Yazarkasa Bağlı Değil Onay Modalı */}
      <Dialog
        open={isFiscalDisconnectedConfirmOpen}
        onClose={() => {
          setIsFiscalDisconnectedConfirmOpen(false);
          setPendingPaymentAction(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'warning.main',
          fontWeight: 'bold'
        }}>
          ⚠️ Yazarkasa Bağlı Değil
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Yazarkasa cihazına bağlanılamadı. Yazarkasa olmadan devam etmek istiyor musunuz?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⚠️ Yazarkasa olmadan yapılan satışlar fişsiz olarak kaydedilecektir.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setIsFiscalDisconnectedConfirmOpen(false);
              setPendingPaymentAction(null);
            }}
            color="inherit"
            variant="outlined"
          >
            İptal
          </Button>
          <Button 
            onClick={() => {
              setIsFiscalDisconnectedConfirmOpen(false);
              if (pendingPaymentAction) {
                pendingPaymentAction();
                setPendingPaymentAction(null);
              }
            }}
            variant="contained"
            color="warning"
          >
            ✅ Yazarkasasız Devam Et
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PartialPaymentModal; 